import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Phone, PhoneCall, PhoneOff, PhoneIncoming, PhoneOutgoing, Settings, Plus, Trash2,
  Play, Pause, Upload, Users, BarChart3, Megaphone, Clock, CheckCircle, XCircle,
  AlertCircle, Rocket, Music, FileAudio, RefreshCw, Search, ArrowRight, Wifi,
  WifiOff, Radio, Headphones, Download, FileText, Eye, Server, Zap, Edit, X, SkipForward,
  MessageSquare, Send, Archive, RotateCcw
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer, AreaChart, Area, LabelList
} from "recharts";

const fmt = (d) => { if (!d) return "-"; try { return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" }); } catch { return d; } };
const fmtDur = (s) => { if (!s) return "0s"; const m = Math.floor(s / 60); const sec = s % 60; return m > 0 ? `${m}m ${sec}s` : `${sec}s`; };

// ============= Sub-components =============

function GatewayStatusPanel({ onRefresh }) {
  const [gwStatus, setGwStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/voip/gateway/status`);
      setGwStatus(r.data);
    } catch { toast.error("Erro ao verificar gateways"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (!gwStatus) return null;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {(gwStatus.gateways || []).map(gw => {
        const ports = gw.ports || [];
        const active = ports.filter(p => p.signal && parseInt(p.signal) > 0).length;
        return (
          <Card key={gw.id} className="border-l-4 border-l-teal-500" data-testid={`gw-status-${gw.id}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Server className="h-4 w-4 text-teal-600" />
                  <span className="font-semibold text-sm">{gw.nome}</span>
                </div>
                <Badge className={ports.length > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                  {ports.length > 0 ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
                  {ports.length > 0 ? "Online" : gw.error || "Offline"}
                </Badge>
              </div>
              {ports.length > 0 && (
                <div className="grid grid-cols-4 gap-1 mt-2">
                  <div className="text-center p-1.5 bg-gray-50 rounded text-xs">
                    <span className="block text-lg font-bold text-gray-700">{ports.length}</span>Portas
                  </div>
                  <div className="text-center p-1.5 bg-green-50 rounded text-xs">
                    <span className="block text-lg font-bold text-green-700">{active}</span>Com Sinal
                  </div>
                  <div className="text-center p-1.5 bg-blue-50 rounded text-xs">
                    <span className="block text-lg font-bold text-blue-700">{ports.filter(p => p.sim_inserted === "Yes").length}</span>SIM
                  </div>
                  <div className="text-center p-1.5 bg-purple-50 rounded text-xs">
                    <span className="block text-lg font-bold text-purple-700">{ports.filter(p => p.registered === "Yes").length}</span>Registrados
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function CampaignReportModal({ campanha, open, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const [rPage, setRPage] = useState(1);
  const [reportView, setReportView] = useState("graficos");
  const perPage = 50;

  const fetchReport = (sf, pg) => {
    if (!campanha) return;
    setLoading(true);
    axios.get(`${API}/voip/campanhas/${campanha.id}/relatorio?status_filter=${sf || "TODOS"}&page=${pg || 1}&per_page=${perPage}`)
      .then(r => setReport(r.data))
      .catch(() => toast.error("Erro ao carregar relatorio"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && campanha) { setStatusFilter("TODOS"); setRPage(1); setReportView("graficos"); fetchReport("TODOS", 1); }
  }, [open, campanha]);

  useEffect(() => {
    if (!open || !campanha) return;
    const isActive = campanha.status === "EM_ANDAMENTO";
    if (!isActive) return;
    const interval = setInterval(() => fetchReport(statusFilter, rPage), 8000);
    return () => clearInterval(interval);
  }, [open, campanha, statusFilter, rPage]);

  const changeFilter = (sf) => { setStatusFilter(sf); setRPage(1); fetchReport(sf, 1); };
  const doChangePage = (pg) => { setRPage(pg); fetchReport(statusFilter, pg); };

  if (!open) return null;
  const s = report?.stats || {};
  const processadas = s.processadas || 0;
  const totalPages = Math.ceil((report?.total_filtradas || 0) / perPage);

  const statusColor = (st) => {
    const m = { ATENDIDA: "bg-green-100 text-green-700 border-green-200", FINALIZADA: "bg-green-100 text-green-700 border-green-200", TRANSFERIDA: "bg-blue-100 text-blue-700 border-blue-200", NAO_ATENDIDA: "bg-yellow-100 text-yellow-700 border-yellow-200", DISCANDO: "bg-blue-50 text-blue-600 border-blue-200", ERRO: "bg-red-100 text-red-700 border-red-200", PENDENTE: "bg-gray-100 text-gray-500 border-gray-200", OCUPADO: "bg-orange-100 text-orange-700 border-orange-200" };
    return m[st] || "bg-gray-100 text-gray-600";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] overflow-y-auto p-0" data-testid="modal-relatorio-campanha">
        {/* Header escuro */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle className="text-lg font-bold text-white">{campanha?.nome}</DialogTitle>
              <DialogDescription className="text-slate-300 text-xs mt-1">Audio: {campanha?.audio_nome || "-"} | Simultaneas: {campanha?.chamadas_simultaneas || 5}</DialogDescription>
            </div>
            <Badge className={campanha?.status === "EM_ANDAMENTO" ? "bg-green-500 text-white animate-pulse" : "bg-slate-500 text-white"}>
              {campanha?.status}
            </Badge>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-300 mb-1">
              <span>{processadas.toLocaleString()} de {(s.total || 0).toLocaleString()}</span>
              <span>{report?.progresso || 0}%</span>
            </div>
            <div className="w-full bg-slate-600 rounded-full h-2.5">
              <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${report?.progresso || 0}%` }} />
            </div>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading && !report ? <div className="text-center py-8 text-gray-400">Carregando...</div> : report && (<>
            {/* Toggle: Graficos x Discagem */}
            <div className="flex items-center gap-2" data-testid="report-view-toggle">
              <button onClick={() => setReportView("graficos")} className={`px-4 py-1.5 text-xs rounded-full font-semibold transition-colors flex items-center gap-1.5 ${reportView === "graficos" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="report-view-graficos"><BarChart3 className="h-3.5 w-3.5" /> Relatorio Grafico</button>
              <button onClick={() => setReportView("discagem")} className={`px-4 py-1.5 text-xs rounded-full font-semibold transition-colors flex items-center gap-1.5 ${reportView === "discagem" ? "bg-slate-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="report-view-discagem"><FileText className="h-3.5 w-3.5" /> Relatorio de Discagem</button>
            </div>

            {reportView === "graficos" ? (<>
            {/* Stats Cards */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {[
                { label: "Total", val: s.total, bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700" },
                { label: "Discando", val: s.discando, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700" },
                { label: "Atendidas", val: s.atendidas, bg: "bg-green-50", border: "border-green-200", text: "text-green-700" },
                { label: "N/Atendidas", val: s.nao_atendidas, bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700" },
                { label: "Erros", val: s.erro, bg: "bg-red-50", border: "border-red-200", text: "text-red-700" },
                { label: "Qualificadas", val: s.qualificadas, bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700" },
              ].map((item, i) => (
                <div key={i} className={`text-center p-2.5 rounded-lg border ${item.bg} ${item.border}`}>
                  <p className={`text-xl font-bold ${item.text}`}>{(item.val || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">{item.label}</p>
                </div>
              ))}
            </div>

            {/* Taxa + Gateways + DTMF */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                <p className="text-2xl font-bold text-emerald-700">{s.taxa_atendimento || 0}%</p>
                <p className="text-[10px] text-gray-500 font-medium uppercase">Taxa Atendimento</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[10px] text-gray-500 font-medium uppercase mb-1.5">Gateways</p>
                {Object.entries(s.gateways || {}).map(([gw, cnt]) => (
                  <div key={gw} className="flex justify-between text-xs mb-0.5">
                    <span className="text-gray-600">{gw}</span>
                    <span className="font-mono font-semibold">{cnt.toLocaleString()}</span>
                  </div>
                ))}
                {Object.keys(s.gateways || {}).length === 0 && <p className="text-xs text-gray-400">-</p>}
              </div>
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-[10px] text-gray-500 font-medium uppercase mb-1.5">DTMF Pressionadas</p>
                {Object.entries(s.dtmf_stats || {}).sort().map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center text-xs mb-0.5">
                    <Badge className="bg-purple-600 text-white text-[10px] px-1.5 py-0">{k}</Badge>
                    <span className="font-mono font-semibold">{v}x</span>
                  </div>
                ))}
                {Object.keys(s.dtmf_stats || {}).length === 0 && <p className="text-xs text-gray-400">-</p>}
              </div>
            </div>

            {/* === Indicadores dos atendidos === */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="relatorio-indicadores">
              {(s.indicadores || []).map((item, i) => (
                <div key={i} className="text-center p-2.5 rounded-lg border bg-white" style={{ borderColor: item.cor + "55" }}>
                  <p className="text-xl font-bold" style={{ color: item.cor }}>{(item.valor || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide leading-tight">{item.nome}</p>
                </div>
              ))}
            </div>

            {/* === Graficos de desempenho === */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="relatorio-graficos">
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs font-semibold text-gray-600 mb-2">Distribuicao por Status</p>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={(s.distribuicao || []).filter(d => d.valor > 0)} dataKey="valor" nameKey="nome" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.nome}: ${(e.valor || 0).toLocaleString()}`} labelLine={false} fontSize={10}>
                      {(s.distribuicao || []).filter(d => d.valor > 0).map((d, i) => <Cell key={i} fill={d.cor} />)}
                    </Pie>
                    <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs font-semibold text-gray-600 mb-2">Funil (Discadas → URA concluida)</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={s.funil || []} layout="vertical" margin={{ left: 10, right: 24, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="etapa" tick={{ fontSize: 10 }} width={84} />
                    <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                    <Bar dataKey="valor" fill="#7c3aed" radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 10, fill: "#475569" }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs font-semibold text-gray-600 mb-2">Evolucao por dia</p>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={s.serie_tempo || []} margin={{ left: -10, right: 8, top: 4, bottom: 4 }}>
                    <defs>
                      <linearGradient id="gDisc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.6} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                      <linearGradient id="gAtend" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#16a34a" stopOpacity={0.6} /><stop offset="95%" stopColor="#16a34a" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="discadas" name="Discadas" stroke="#3b82f6" fill="url(#gDisc)" strokeWidth={2}>
                      <LabelList dataKey="discadas" position="top" fontSize={9} fill="#2563eb" formatter={(v) => (v || 0).toLocaleString()} />
                    </Area>
                    <Area type="monotone" dataKey="atendidas" name="Atendidas" stroke="#16a34a" fill="url(#gAtend)" strokeWidth={2}>
                      <LabelList dataKey="atendidas" position="bottom" fontSize={9} fill="#15803d" formatter={(v) => (v || 0).toLocaleString()} />
                    </Area>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* === Detalhe das Atendidas === */}
            <div className="p-3 bg-white rounded-lg border" data-testid="relatorio-detalhe-atendidas">
              <p className="text-xs font-semibold text-gray-600 mb-2">Detalhe das Atendidas ({(s.atendidas || 0).toLocaleString()})</p>
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={s.detalhe_atendidas || []} layout="vertical" margin={{ left: 10, right: 36, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="nome" tick={{ fontSize: 10 }} width={140} />
                  <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                  <Bar dataKey="valor" radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 11, fill: "#475569" }}>
                    {(s.detalhe_atendidas || []).map((d, i) => <Cell key={i} fill={d.cor} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* === Solicitacoes da URA: classificacao + tag cliente + whatsapp === */}
            <div className="border rounded-lg p-3 bg-slate-50/60" data-testid="relatorio-solicitacoes-ura">
              <p className="text-xs font-bold text-slate-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                Solicitacoes da URA (qualificados)
                <span className="text-[11px] font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">{(s.sol_total || 0).toLocaleString()} total</span>
              </p>
              {(s.sol_total || 0) === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">Nenhuma solicitacao da URA nesta campanha</p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Por Segmento</p>
                    <ResponsiveContainer width="100%" height={Math.max(160, (s.sol_por_segmento || []).length * 26)}>
                      <BarChart data={s.sol_por_segmento || []} layout="vertical" margin={{ left: 10, right: 28, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="nome" tick={{ fontSize: 10 }} width={110} />
                        <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                        <Bar dataKey="valor" fill="#8b5cf6" radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 10, fill: "#475569" }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Por Fila</p>
                    <ResponsiveContainer width="100%" height={Math.max(160, (s.sol_por_fila || []).length * 26)}>
                      <BarChart data={s.sol_por_fila || []} layout="vertical" margin={{ left: 10, right: 28, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="nome" tick={{ fontSize: 10 }} width={110} />
                        <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                        <Bar dataKey="valor" fill="#0ea5e9" radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 10, fill: "#475569" }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Por Tag do Cliente (CRM)</p>
                    <ResponsiveContainer width="100%" height={Math.max(160, (s.sol_por_tag || []).length * 26)}>
                      <BarChart data={s.sol_por_tag || []} layout="vertical" margin={{ left: 10, right: 28, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="nome" tick={{ fontSize: 10 }} width={120} />
                        <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                        <Bar dataKey="valor" radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 10, fill: "#475569" }}>
                          {(s.sol_por_tag || []).map((d, i) => <Cell key={i} fill={d.cor || "#8b5cf6"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Notificacao via WhatsApp</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={(s.sol_whatsapp || []).filter(d => d.valor > 0)} dataKey="valor" nameKey="nome" cx="50%" cy="50%" innerRadius={45} outerRadius={75} label={(e) => `${e.nome}: ${(e.valor || 0).toLocaleString()}`} labelLine={false} fontSize={10}>
                          {(s.sol_whatsapp || []).filter(d => d.valor > 0).map((d, i) => <Cell key={i} fill={d.cor} />)}
                        </Pie>
                        <RTooltip formatter={(v) => (v || 0).toLocaleString()} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            </>) : (<>
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-xs text-gray-500 mr-1">Filtrar:</span>
              {["TODOS","DISCANDO","ATENDIDA","NAO_ATENDIDA","ERRO","PENDENTE"].map(f => (
                <button key={f} onClick={() => changeFilter(f)}
                  className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition-all ${statusFilter === f ? "bg-slate-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {f === "TODOS" ? "Todos" : f === "NAO_ATENDIDA" ? "N/Atendida" : f.charAt(0) + f.slice(1).toLowerCase()}
                  {f !== "TODOS" && ` (${f === "DISCANDO" ? (s.discando||0) : f === "ATENDIDA" ? (s.atendidas||0) : f === "NAO_ATENDIDA" ? (s.nao_atendidas||0) : f === "ERRO" ? (s.erro||0) : f === "PENDENTE" ? (s.pendentes||0) : 0})`}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[45vh] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50 z-10">
                    <TableRow>
                      <TableHead className="text-xs font-semibold">Numero</TableHead>
                      <TableHead className="text-xs font-semibold">Contato</TableHead>
                      <TableHead className="text-xs font-semibold">Status</TableHead>
                      <TableHead className="text-xs font-semibold">Data/Hora</TableHead>
                      <TableHead className="text-xs font-semibold">Gateway</TableHead>
                      <TableHead className="text-xs font-semibold text-center">DTMF</TableHead>
                      <TableHead className="text-xs font-semibold">Duracao</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(report.chamadas || []).length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-6 text-sm">Nenhuma chamada neste filtro</TableCell></TableRow>
                    ) : report.chamadas.map(c => (
                      <TableRow key={c.id} className="hover:bg-slate-50/50">
                        <TableCell className="font-mono text-xs py-1.5">{c.numero}</TableCell>
                        <TableCell className="text-xs py-1.5 max-w-[120px] truncate">{c.nome_contato || "-"}</TableCell>
                        <TableCell className="py-1.5"><Badge className={`text-[10px] px-1.5 py-0 border ${statusColor(c.status)}`}>{c.status}</Badge></TableCell>
                        <TableCell className="text-[10px] text-gray-500 py-1.5 whitespace-nowrap">{(c.updated_at || c.created_at) ? new Date(c.updated_at || c.created_at).toLocaleString("pt-BR", {day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit",second:"2-digit",timeZone:"America/Sao_Paulo"}) : "-"}</TableCell>
                        <TableCell className="text-[10px] text-gray-500 py-1.5">{c.gateway || "-"}</TableCell>
                        <TableCell className="text-center py-1.5">{c.dtmf_pressionado ? <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0">{c.dtmf_pressionado}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                        <TableCell className="text-xs text-gray-500 py-1.5">{fmtDur(c.duracao)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t text-xs">
                  <span className="text-gray-500">{(report.total_filtradas || 0).toLocaleString()} registros</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => doChangePage(Math.max(1, rPage - 1))} disabled={rPage <= 1} className="px-2 py-0.5 rounded bg-white border disabled:opacity-30 hover:bg-gray-50">Ant</button>
                    <span className="px-2 text-gray-600">{rPage} / {totalPages}</span>
                    <button onClick={() => doChangePage(Math.min(totalPages, rPage + 1))} disabled={rPage >= totalPages} className="px-2 py-0.5 rounded bg-white border disabled:opacity-30 hover:bg-gray-50">Prox</button>
                  </div>
                </div>
              )}
            </div>
            </>)}
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============= Main Component =============

export default function CentralVoIP() {
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ configurado: false });
  const [audios, setAudios] = useState([]);
  const [campanhas, setCampanhas] = useState([]);
  const [chamadas, setChamadas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [relatorio, setRelatorio] = useState({});
  const [receptivoConfig, setReceptivoConfig] = useState(null);
  const [uraSolicitacoes, setUraSolicitacoes] = useState([]);
  const [uraFiltroAtend, setUraFiltroAtend] = useState("TODOS");
  const [backfillRodando, setBackfillRodando] = useState(false);
  const rodarBackfillCadastro = async () => {
    try {
      setBackfillRodando(true);
      toast.info("Iniciando cadastro automatico das solicitacoes (Nova Vida)...");
      await axios.post(`${API}/voip/receptivo/solicitacoes/backfill-cadastro`);
      const poll = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/voip/receptivo/solicitacoes/backfill-cadastro/status`);
          if (st.data && !st.data.rodando) {
            clearInterval(poll);
            setBackfillRodando(false);
            const sol = await axios.get(`${API}/voip/receptivo/solicitacoes?limit=500`);
            setUraSolicitacoes(sol.data || []);
            toast.success(`Concluido: ${st.data.cadastrados} cadastrados, ${st.data.ja_cadastrados} ja existiam, ${st.data.vinculo_telefone || 0} por telefone, ${st.data.ignorados} ignorados, ${st.data.nao_localizados || 0} nao localizados, ${st.data.invalidos} CPF invalido`);
          }
        } catch (e) { clearInterval(poll); setBackfillRodando(false); }
      }, 3000);
    } catch (e) { setBackfillRodando(false); toast.error("Erro ao iniciar cadastro automatico"); }
  };
  const [uraTransferidas, setUraTransferidas] = useState([]);
  const [ocupados, setOcupados] = useState([]);
  const [atendidasNt, setAtendidasNt] = useState([]);
  const [atendidasTr, setAtendidasTr] = useState([]);

  // SMS por etapa de ligacao
  const [smsEtapa, setSmsEtapa] = useState({ open: false, etapa: "", label: "" });
  const [smsEtapaTemplate, setSmsEtapaTemplate] = useState("");
  const [smsEtapaPeriodo, setSmsEtapaPeriodo] = useState("hoje");
  const [smsEtapaCount, setSmsEtapaCount] = useState(null);
  const [smsEtapaCounting, setSmsEtapaCounting] = useState(false);
  const [smsEtapaSending, setSmsEtapaSending] = useState(false);
  const [smsEtapaCampanha, setSmsEtapaCampanha] = useState("todas");
  const [smsEtapaNaoEnviados, setSmsEtapaNaoEnviados] = useState(true);
  const [autoSmsAtivo, setAutoSmsAtivo] = useState(false);
  const [autoSmsTemplate, setAutoSmsTemplate] = useState("");
  const [autoSmsPeriodo, setAutoSmsPeriodo] = useState("hoje");
  const [autoSmsCfgAll, setAutoSmsCfgAll] = useState({ etapas: {}, intervalo_min: 5 });
  const [autoSmsSaving, setAutoSmsSaving] = useState(false);
  const [receptivoChamadas, setReceptivoChamadas] = useState([]);

  // Modals
  const [showConfig, setShowConfig] = useState(false);
  const [showNovaCampanha, setShowNovaCampanha] = useState(false);
  const [showUploadAudio, setShowUploadAudio] = useState(false);
  const [showFluxo, setShowFluxo] = useState(null);
  const [showReportCamp, setShowReportCamp] = useState(null);
  const [campView, setCampView] = useState("ativas");
  const [showImportContatos, setShowImportContatos] = useState(null);
  const [showEditarCampanha, setShowEditarCampanha] = useState(false);
  const [editarCampanhaData, setEditarCampanhaData] = useState(null);

  // Forms
  const [configForm, setConfigForm] = useState({
    gateway_ip: "", gateway_port: "80", sip_port: "5060",
    ami_host: "", ami_port: "5038", ami_user: "", ami_secret: "",
    ramal_saida: "", numero_transferencia: ""
  });
  const defaultDtmf = {
    "1": { acao: "enviar_sms_whatsapp", destino: "", descricao: "Tenho interesse" },
    "2": { acao: "enviar_sms", destino: "", descricao: "Mais informacoes" },
    "9": { acao: "blacklist", destino: "", descricao: "Nao ligar novamente" }
  };
  const [campanhaForm, setCampanhaForm] = useState({
    nome: "", audio_id: "", grupo_contato: "", tipo_telefone: "todos", whatsapp_status: "todos", sms_status: "todos", acao: "todos", tentativas_max: 3,
    intervalo_tentativas: 30, chamadas_simultaneas: 32,
    horario_inicio: "09:00", horario_fim: "18:00", fluxo_dtmf: { ...defaultDtmf },
    screen_pop: {
      ativo: false, titulo: "", mensagem: "Temos uma oferta especial para voce!",
      empresa: "StiloCred", enviar_sms: true, enviar_wpp: true,
      sms_msg: "Voce recebeu uma ligacao da StiloCred! Acesse: {link}",
      wpp_msg: "Voce esta recebendo uma ligacao nossa! Acesse o link: {link}",
      botoes: [
        { label: "Sim, tenho interesse!", acao: "interesse", estilo: "primary" },
        { label: "Agora nao", acao: "recusa", estilo: "secondary" }
      ]
    }
  });
  const [campanhaPreview, setCampanhaPreview] = useState({ total_no_grupo: 0, total_apos_filtros: 0, total_validos: 0 });
  useEffect(() => {
    if (!showNovaCampanha || !campanhaForm.grupo_contato) { setCampanhaPreview({ total_no_grupo: 0, total_apos_filtros: 0, total_validos: 0 }); return; }
    const t = setTimeout(async () => {
      try {
        const grupo = campanhaForm.grupo_contato === " " ? "" : campanhaForm.grupo_contato;
        const res = await axios.post(`${API}/sms/contatos/preview-filtro`, {
          grupo_contato: grupo,
          tipo_telefone: campanhaForm.tipo_telefone,
          whatsapp_status: campanhaForm.whatsapp_status,
          sms_status: campanhaForm.sms_status,
          acao: campanhaForm.acao,
        });
        setCampanhaPreview(res.data || {});
      } catch (e) {}
    }, 400);
    return () => clearTimeout(t);
  }, [showNovaCampanha, campanhaForm.grupo_contato, campanhaForm.tipo_telefone, campanhaForm.whatsapp_status, campanhaForm.sms_status, campanhaForm.acao]);
  // Modal: adicionar mais contatos em campanha existente
  const [adicionarContatosModal, setAdicionarContatosModal] = useState(null);
  const [adicionarForm, setAdicionarForm] = useState({ tipo_telefone: "todos", whatsapp_status: "todos", sms_status: "todos", acao: "todos", limite_contatos: 0 });
  const [adicionandoContatos, setAdicionandoContatos] = useState(false);
  const adicionarMaisContatos = async () => {
    if (adicionandoContatos || !adicionarContatosModal) return;
    setAdicionandoContatos(true);
    try {
      const res = await axios.post(`${API}/voip/campanhas/${adicionarContatosModal.id}/adicionar-contatos`, {
        grupo_contato: adicionarContatosModal.grupo_contato || "",
        ...adicionarForm
      });
      if (res.data?.success) {
        toast.success(`${res.data.adicionados} contatos adicionados! Novo total: ${res.data.novo_total}`);
        setAdicionarContatosModal(null);
        setAdicionarForm({ tipo_telefone: "todos", whatsapp_status: "todos", sms_status: "todos", acao: "todos", limite_contatos: 0 });
        carregarTudo();
      } else {
        toast.warning(res.data?.message || "Nenhum contato adicionado");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao adicionar contatos"); }
    finally { setAdicionandoContatos(false); }
  };
  const [audioForm, setAudioForm] = useState({ nome: "", descricao: "" });
  const [audioFile, setAudioFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [wppFlows, setWppFlows] = useState([]);
  const [wppRespostas, setWppRespostas] = useState([]);

  // Filters
  const [filtroCampChamada, setFiltroCampChamada] = useState("todos");
  const [filtroStatusChamada, setFiltroStatusChamada] = useState("todos");

  useEffect(() => { carregarTudo(); }, []);

  useEffect(() => {
    const carregarExtras = async () => {
      try {
        const [ocR, atR, trR] = await Promise.all([
          axios.get(`${API}/voip/chamadas?status=OCUPADO&limit=500`).catch(() => ({ data: [] })),
          axios.get(`${API}/voip/chamadas?status=ATENDIDA&limit=500`).catch(() => ({ data: [] })),
          axios.get(`${API}/voip/atendidas-transferidas?limit=500`).catch(() => ({ data: [] })),
        ]);
        setOcupados(ocR.data || []);
        setAtendidasNt(atR.data || []);
        setAtendidasTr(trR.data || []);
      } catch (e) { /* noop */ }
    };
    carregarExtras();
    const iv = setInterval(carregarExtras, 30000);
    return () => clearInterval(iv);
  }, []);

  const carregarTudo = async () => {
    setLoading(true);
    try {
      const [cfgR, audR, campR, chamR, grpR, relR, recR, dtmfR, solR, trR] = await Promise.all([
        axios.get(`${API}/voip/config`),
        axios.get(`${API}/voip/audios`),
        axios.get(`${API}/voip/campanhas?arquivadas=todas`),
        axios.get(`${API}/voip/chamadas?limit=300`),
        axios.get(`${API}/sms/contatos/grupos`).catch(() => ({ data: [] })),
        axios.get(`${API}/voip/relatorio`),
        axios.get(`${API}/voip/receptivo/config`),
        axios.get(`${API}/voip/opcoes-dtmf`).catch(() => ({ data: { sms_templates: [], wpp_flows: [], wpp_respostas: [] } })),
        axios.get(`${API}/voip/receptivo/solicitacoes?limit=500`).catch(() => ({ data: [] })),
        axios.get(`${API}/voip/receptivo/transferidas-incompletas?limit=500`).catch(() => ({ data: [] }))
      ]);
      setConfig(cfgR.data);
      setConfigForm({
        gateway_ip: cfgR.data.gateway_ip || "", gateway_port: cfgR.data.gateway_port || "80",
        sip_port: cfgR.data.sip_port || "5060",
        ami_host: cfgR.data.ami_host || "", ami_port: cfgR.data.ami_port || "5038",
        ami_user: cfgR.data.ami_user || "", ami_secret: cfgR.data.ami_secret || "",
        ramal_saida: cfgR.data.ramal_saida || "", numero_transferencia: cfgR.data.numero_transferencia || ""
      });
      setAudios(audR.data); setCampanhas(campR.data); setChamadas(chamR.data);
      setGrupos(grpR.data); setRelatorio(relR.data); setReceptivoConfig(recR.data);
      setUraSolicitacoes(solR.data || []);
      setUraTransferidas(trR?.data || []);
      if (dtmfR.data) {
        setSmsTemplates(dtmfR.data.sms_templates || []);
        setWppFlows(dtmfR.data.wpp_flows || []);
        setWppRespostas(dtmfR.data.wpp_respostas || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ===== SMS por etapa de ligacao =====
  const contarSmsEtapa = async (etapa, periodo, campanha, naoEnviados) => {
    setSmsEtapaCounting(true);
    try {
      const r = await axios.post(`${API}/voip/sms-etapa/contar`, {
        etapa, periodo,
        campanha_id: campanha && campanha !== "todas" ? campanha : null,
        apenas_nao_enviados: !!naoEnviados,
      });
      setSmsEtapaCount(r.data?.total ?? 0);
    } catch { setSmsEtapaCount(null); }
    finally { setSmsEtapaCounting(false); }
  };

  const abrirSmsEtapa = async (etapa, label) => {
    setSmsEtapaTemplate("");
    setSmsEtapaPeriodo("hoje");
    setSmsEtapaCampanha("todas");
    setSmsEtapaNaoEnviados(true);
    setSmsEtapaCount(null);
    setAutoSmsAtivo(false);
    setAutoSmsTemplate("");
    setAutoSmsPeriodo("hoje");
    setSmsEtapa({ open: true, etapa, label });
    try {
      const r = await axios.get(`${API}/voip/sms-etapa/auto-config`);
      const all = r.data || { etapas: {}, intervalo_min: 5 };
      setAutoSmsCfgAll(all);
      const ec = (all.etapas || {})[etapa] || {};
      setAutoSmsAtivo(!!ec.ativo);
      setAutoSmsTemplate(ec.template_id || "");
      setAutoSmsPeriodo(ec.periodo || "hoje");
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (smsEtapa.open && smsEtapa.etapa) contarSmsEtapa(smsEtapa.etapa, smsEtapaPeriodo, smsEtapaCampanha, smsEtapaNaoEnviados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsEtapa.open, smsEtapa.etapa, smsEtapaPeriodo, smsEtapaCampanha, smsEtapaNaoEnviados]);

  const enviarSmsEtapa = async () => {
    if (!smsEtapaTemplate) { toast.error("Selecione um modelo de SMS"); return; }
    if (!smsEtapaCount) { toast.error("Nenhum numero para esta etapa/periodo"); return; }
    setSmsEtapaSending(true);
    try {
      const cr = await axios.post(`${API}/voip/sms-etapa/criar-campanha`, {
        etapa: smsEtapa.etapa, periodo: smsEtapaPeriodo, template_id: smsEtapaTemplate,
        campanha_id: smsEtapaCampanha && smsEtapaCampanha !== "todas" ? smsEtapaCampanha : null,
        apenas_nao_enviados: smsEtapaNaoEnviados,
      });
      await axios.post(`${API}/sms/campanhas/${cr.data.campanha_id}/enviar`);
      toast.success(`SMS iniciado para ${cr.data.total} numeros (campanha "${cr.data.nome}")`);
      setSmsEtapa({ open: false, etapa: "", label: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar SMS");
    } finally { setSmsEtapaSending(false); }
  };

  const salvarAutoSms = async () => {
    if (autoSmsAtivo && !autoSmsTemplate) { toast.error("Selecione um modelo para o envio automatico"); return; }
    setAutoSmsSaving(true);
    try {
      const etapas = { ...(autoSmsCfgAll.etapas || {}) };
      etapas[smsEtapa.etapa] = { ativo: autoSmsAtivo, template_id: autoSmsTemplate, periodo: autoSmsPeriodo };
      const r = await axios.post(`${API}/voip/sms-etapa/auto-config`, { etapas, intervalo_min: autoSmsCfgAll.intervalo_min || 5 });
      setAutoSmsCfgAll({ etapas: r.data.etapas, intervalo_min: r.data.intervalo_min });
      toast.success(autoSmsAtivo ? "Envio automatico ATIVADO para esta etapa" : "Envio automatico desativado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar configuracao automatica"); }
    finally { setAutoSmsSaving(false); }
  };

  // Config
  const salvarConfig = async () => {
    try {
      await axios.put(`${API}/voip/config`, configForm);
      toast.success("Configuracao salva!"); setShowConfig(false); carregarTudo();
    } catch { toast.error("Erro ao salvar"); }
  };

  const testarAMI = async () => {
    try {
      const r = await axios.post(`${API}/voip/ami/test`);
      if (r.data.success) toast.success("Conexao AMI OK!");
      else toast.error(r.data.error || "Falha na conexao AMI");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao testar AMI"); }
  };

  // Audio
  const uploadAudio = async () => {
    if (!audioFile) { toast.error("Selecione um arquivo de audio"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", audioFile);
      await axios.post(`${API}/voip/audios?nome=${encodeURIComponent(audioForm.nome || audioFile.name)}&descricao=${encodeURIComponent(audioForm.descricao)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio enviado!"); setShowUploadAudio(false);
      setAudioForm({ nome: "", descricao: "" }); setAudioFile(null); carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro no upload"); }
    finally { setUploading(false); }
  };

  const excluirAudio = async (id) => {
    try { await axios.delete(`${API}/voip/audios/${id}`); toast.success("Audio excluido!"); carregarTudo(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const enviarIVRGateway = async (id) => {
    try {
      const r = await axios.post(`${API}/voip/gateway/ivr-upload/${id}`);
      const ok = (r.data.results || []).some(g => g.success);
      if (ok) toast.success("Audio enviado ao gateway DINSTAR!");
      else toast.error("Falha ao enviar audio ao gateway");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
  };

  // Campanhas
  const salvarCampanha = async () => {
    try {
      if (!campanhaForm.nome) { toast.error("Nome e obrigatorio"); return; }
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const audioSel = audios.find(a => a.id === campanhaForm.audio_id);
      await axios.post(`${API}/voip/campanhas`, {
        ...campanhaForm, audio_nome: audioSel?.nome || "", created_by: usuario.nome || "admin"
      });
      toast.success("Campanha criada!"); setShowNovaCampanha(false);
      setCampanhaForm({
        nome: "", audio_id: "", grupo_contato: "", tipo_telefone: "todos", whatsapp_status: "todos", sms_status: "todos", acao: "todos", tentativas_max: 3,
        intervalo_tentativas: 30, chamadas_simultaneas: 32,
        horario_inicio: "09:00", horario_fim: "18:00", fluxo_dtmf: { ...defaultDtmf }
      });
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar"); }
  };

  const [gerandoCltVoip, setGerandoCltVoip] = useState(false);
  const gerarCampanhaCltComOferta = async () => {
    try {
      setGerandoCltVoip(true);
      const prev = await axios.get(`${API}/voip/campanhas/prospecta-clt-com-oferta/preview`);
      const eleg = prev.data?.elegiveis || 0;
      if (!eleg) { toast.error("Nenhum lead com oferta real e telefone válido no momento."); return; }
      if (!window.confirm(`Criar campanha VoIP (URA reversa) para ${eleg} lead(s) CLT COM oferta real?\n\nÁudio: ${prev.data?.audio_nome || "leilão"}. Leads SEM oferta NÃO entram (seguem no fluxo atual).`)) return;
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const res = await axios.post(`${API}/voip/campanhas/prospecta-clt-com-oferta`, { created_by: usuario.nome || "admin" });
      toast.success(`Campanha "${res.data.nome}" criada com ${res.data.total_contatos} contatos! Revise e clique em Iniciar.`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar campanha CLT"); }
    finally { setGerandoCltVoip(false); }
  };

  const iniciarCampanha = async (id) => {
    try {
      const res = await axios.post(`${API}/voip/campanhas/${id}/iniciar`);
      toast.success(res.data.message || "Campanha iniciada!"); carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao iniciar"); }
  };

  const pausarCampanha = async (id) => {
    try { await axios.post(`${API}/voip/campanhas/${id}/pausar`); toast.success("Campanha pausada!"); carregarTudo(); }
    catch { toast.error("Erro ao pausar"); }
  };

  const finalizarCampanha = async (id) => {
    try { await axios.post(`${API}/voip/campanhas/${id}/finalizar`); toast.success("Campanha finalizada!"); carregarTudo(); }
    catch { toast.error("Erro ao finalizar"); }
  };

  const excluirCampanha = async (id) => {
    if (!window.confirm("Excluir esta campanha e todas as chamadas associadas?")) return;
    try { await axios.delete(`${API}/voip/campanhas/${id}`); toast.success("Campanha excluida!"); carregarTudo(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const reenviarCampanha = async (id) => {
    if (!window.confirm("Reenviar campanha do inicio? Todas as chamadas serao reiniciadas.")) return;
    try {
      const res = await axios.post(`${API}/voip/campanhas/${id}/reenviar`);
      toast.success(res.data.message || "Campanha reenviada!"); carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reenviar"); }
  };

  const retomarDeOndeParou = async (id) => {
    try {
      const res = await axios.post(`${API}/voip/campanhas/${id}/retomar-de-onde-parou`);
      const data = res.data;
      toast.success(`Retomando: ${data.erros_resetados || 0} erros resetados, ${data.pendentes || 0} pendentes`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao retomar"); }
  };

  const rediscarGrupo = async (id, grupo, label) => {
    if (!window.confirm(`Rediscar todos os "${label}" desta campanha? Eles voltarao para a fila de discagem.`)) return;
    try {
      const res = await axios.post(`${API}/voip/campanhas/${id}/rediscar-grupo?grupo=${grupo}`);
      toast.success(`${res.data.resetadas || 0} "${label}" voltaram para a fila`);
      carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao rediscar"); }
  };

  const arquivarCampanha = async (id) => {
    if (!window.confirm("Arquivar esta campanha? Ela vai PARAR de discar e sair da lista de ativas (voce pode restaurar depois).")) return;
    try { await axios.post(`${API}/voip/campanhas/${id}/arquivar`); toast.success("Campanha arquivada!"); carregarTudo(); }
    catch { toast.error("Erro ao arquivar"); }
  };

  const desarquivarCampanha = async (id) => {
    try { await axios.post(`${API}/voip/campanhas/${id}/desarquivar`); toast.success("Campanha restaurada para Ativas!"); carregarTudo(); }
    catch { toast.error("Erro ao restaurar"); }
  };

  const abrirEditarCampanha = (c) => {
    setEditarCampanhaData({
      id: c.id, nome: c.nome, audio_id: c.audio_id || "", audio_nome: c.audio_nome || "",
      grupo_contato: c.grupo_contato || "", tentativas_max: c.tentativas_max || 3,
      intervalo_tentativas: c.intervalo_tentativas || 30, chamadas_simultaneas: c.chamadas_simultaneas || 5,
      horario_inicio: c.horario_inicio || "09:00", horario_fim: c.horario_fim || "18:00",
      fluxo_dtmf: c.fluxo_dtmf || { ...defaultDtmf },
      screen_pop: c.screen_pop || { ativo: false, titulo: "", mensagem: "", empresa: "StiloCred", enviar_sms: true, enviar_wpp: true, sms_msg: "", wpp_msg: "", botoes: [] }
    });
    setShowEditarCampanha(true);
  };

  const salvarEdicaoCampanha = async () => {
    if (!editarCampanhaData) return;
    try {
      const audioSel = audios.find(a => a.id === editarCampanhaData.audio_id);
      await axios.put(`${API}/voip/campanhas/${editarCampanhaData.id}`, {
        ...editarCampanhaData, audio_nome: audioSel?.nome || editarCampanhaData.audio_nome || ""
      });
      toast.success("Campanha atualizada!"); setShowEditarCampanha(false); carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar"); }
  };

  // Auto-refresh campanhas ativas
  useEffect(() => {
    const hasActive = campanhas.some(c => c.status === "EM_ANDAMENTO");
    if (!hasActive) return;
    const interval = setInterval(() => { carregarTudo(); }, 5000);
    return () => clearInterval(interval);
  }, [campanhas.map(c => c.status).join(",")]);

  const importarContatos = async (campId) => {
    if (!importFile) { toast.error("Selecione um arquivo"); return; }
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const r = await axios.post(`${API}/voip/campanhas/${campId}/importar-contatos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.total_importados} contatos importados!`);
      setShowImportContatos(null); setImportFile(null); carregarTudo();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro na importacao"); }
  };

  // Receptivo
  const salvarReceptivo = async () => {
    try {
      await axios.put(`${API}/voip/receptivo/config`, receptivoConfig);
      toast.success("Configuracao do receptivo salva!");
    } catch { toast.error("Erro ao salvar"); }
  };

  const chamadasFiltradas = chamadas
    .filter(c => filtroCampChamada === "todos" || c.campanha_id === filtroCampChamada)
    .filter(c => filtroStatusChamada === "todos" || c.status === filtroStatusChamada);

  const statusColors = {
    PENDENTE: "bg-gray-100 text-gray-700", DISCANDO: "bg-blue-100 text-blue-700",
    ATENDIDA: "bg-green-100 text-green-700", FINALIZADA: "bg-emerald-100 text-emerald-700",
    NAO_ATENDIDA: "bg-yellow-100 text-yellow-700", OCUPADO: "bg-orange-100 text-orange-700",
    ERRO: "bg-red-100 text-red-700", TRANSFERIDA: "bg-indigo-100 text-indigo-700",
    AGUARDANDO_INFRA: "bg-amber-100 text-amber-700"
  };
  const campStatusColors = {
    RASCUNHO: "bg-gray-100 text-gray-700", EM_ANDAMENTO: "bg-blue-100 text-blue-700",
    PAUSADA: "bg-yellow-100 text-yellow-700", FINALIZADA: "bg-green-100 text-green-700",
    ERRO: "bg-red-100 text-red-700"
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="voip-title">Central VoIP</h1>
          <p className="text-sm text-gray-500 mt-1">URA Reversa + Central Receptivo</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={config.configurado ? "default" : "destructive"} className={`gap-1 ${config.configurado ? "bg-green-600" : ""}`} data-testid="badge-gateway-status">
            {config.configurado ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {config.configurado ? "Gateway Online" : "Gateway Offline"}
          </Badge>
          {config.ami_configurado && (
            <Badge className="gap-1 bg-teal-600" data-testid="badge-ami-status">
              <Zap className="h-3 w-3" /> AMI Conectado
            </Badge>
          )}
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowConfig(true)} data-testid="btn-config-voip">
            <Settings className="h-4 w-4" /> Configurar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
        {[
          { label: "Campanhas", val: relatorio.total_campanhas || 0, color: "border-l-blue-500", textColor: "text-blue-700", Icon: Megaphone, iconColor: "text-blue-200" },
          { label: "Chamadas Hoje", val: relatorio.chamadas_hoje || 0, color: "border-l-green-500", textColor: "text-green-700", Icon: PhoneCall, iconColor: "text-green-200" },
          { label: "Atendidas Hoje", val: relatorio.atendidas_hoje || 0, color: "border-l-emerald-500", textColor: "text-emerald-700", Icon: PhoneIncoming, iconColor: "text-emerald-200" },
          { label: "Qualif. Hoje", val: relatorio.qualificadas_hoje || 0, color: "border-l-purple-500", textColor: "text-purple-700", Icon: CheckCircle, iconColor: "text-purple-200" },
          { label: "Taxa Atend. Hoje", val: `${relatorio.taxa_hoje || 0}%`, color: "border-l-orange-500", textColor: "text-orange-700", Icon: BarChart3, iconColor: "text-orange-200" },
        ].map((s, i) => (
          <Card key={i} className={`border-l-4 ${s.color}`}>
            <CardContent className="pt-3 pb-2 px-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                  <p className={`text-xl font-bold ${s.textColor}`}>{s.val}</p>
                </div>
                <s.Icon className={`h-7 w-7 ${s.iconColor}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <div className="overflow-x-auto">
          <TabsList className="inline-flex gap-1 bg-transparent p-1 min-w-max">
            {[
              { val: "dashboard", Icon: Phone, color: "blue", label: "Dashboard" },
              { val: "campanhas", Icon: Megaphone, color: "purple", label: "Campanhas URA" },
              { val: "audios", Icon: Music, color: "green", label: "Audios" },
              { val: "chamadas", Icon: PhoneCall, color: "orange", label: "Chamadas" },
              { val: "receptivo", Icon: Headphones, color: "teal", label: "Receptivo" },
              { val: "relatorios", Icon: BarChart3, color: "slate", label: "Relatorios" },
              { val: "solicitacoes", Icon: FileText, color: "purple", label: "Solicitacoes da URA" },
              { val: "transferidas", Icon: FileText, color: "orange", label: "TRANSFERIDA (URA nao concluida)" },
              { val: "ocupados", Icon: FileText, color: "orange", label: "Ocupados/Rejeitados" },
              { val: "atendidas_nt", Icon: FileText, color: "green", label: "Atendidas (nao transf.)" },
              { val: "atendidas_tr", Icon: FileText, color: "blue", label: "Atendidas (transferida)" },
            ].map(t => (
              <TabsTrigger key={t.val} value={t.val} className={`gap-1.5 px-3 py-2 data-[state=active]:bg-${t.color}-100`}>
                <t.Icon className={`h-4 w-4 text-${t.color}-600`} />
                <span className={`text-${t.color}-600 font-medium text-sm`}>{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* === Dashboard === */}
        <TabsContent value="dashboard" className="space-y-4" data-testid="tab-dashboard-voip">
          <GatewayStatusPanel />
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Megaphone className="h-4 w-4 text-purple-600" /> Campanhas Recentes</CardTitle></CardHeader>
              <CardContent>
                {campanhas.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground"><Megaphone className="h-8 w-8 mx-auto mb-2 opacity-20" /><p className="text-sm">Nenhuma campanha criada</p></div>
                ) : (
                  <div className="space-y-2">
                    {campanhas.slice(0, 5).map(c => (
                      <div key={c.id} className="p-3 rounded-lg border bg-white flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{c.nome}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge className={campStatusColors[c.status] || "bg-gray-100"} variant="outline">{c.status}</Badge>
                            <span className="text-xs text-gray-500">{c.total_contatos} contatos</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {c.status === "RASCUNHO" && <Button size="sm" className="h-7 gap-1 bg-green-600 hover:bg-green-700 text-xs" onClick={() => iniciarCampanha(c.id)}><Rocket className="h-3 w-3" /> Iniciar</Button>}
                          {c.status === "EM_ANDAMENTO" && <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => pausarCampanha(c.id)}><Pause className="h-3 w-3" /> Pausar</Button>}
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setShowReportCamp(c)}><Eye className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Phone className="h-4 w-4 text-blue-600" /> Como Funciona</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { Icon: PhoneOutgoing, color: "text-blue-600 bg-blue-50", title: "1. Disparo Automatico", desc: "Sistema liga para a lista de contatos via DINSTAR" },
                    { Icon: Music, color: "text-green-600 bg-green-50", title: "2. Mensagem Gravada", desc: "Reproduz audio quando o cliente atende" },
                    { Icon: Radio, color: "text-purple-600 bg-purple-50", title: "3. Qualificacao DTMF", desc: "Cliente digita tecla para confirmar interesse" },
                    { Icon: ArrowRight, color: "text-orange-600 bg-orange-50", title: "4. Acao Pos-Qualificacao", desc: "Transfere ramal, envia WhatsApp ou registra CRM" }
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${step.color}`}><step.Icon className="h-4 w-4" /></div>
                      <div><p className="font-semibold text-sm">{step.title}</p><p className="text-xs text-gray-500">{step.desc}</p></div>
                    </div>
                  ))}
                </div>
                {!config.ami_configurado && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Asterisk AMI nao configurado
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Configure o Asterisk na sua rede local para ativar as chamadas. Clique em "Configurar" para inserir os dados do AMI.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === Campanhas URA === */}
        <TabsContent value="campanhas" className="space-y-4" data-testid="tab-campanhas-ura">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-purple-600" /> Campanhas URA Reversa</CardTitle>
                <CardDescription>Crie e gerencie campanhas de discagem automatica</CardDescription>
              </div>
              <Button className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={gerarCampanhaCltComOferta} disabled={gerandoCltVoip} data-testid="btn-gerar-voip-clt-oferta"><Megaphone className="h-4 w-4" /> {gerandoCltVoip ? "Gerando..." : "Gerar CLT (Com Oferta)"}</Button>
              <Button className="gap-1 bg-purple-600 hover:bg-purple-700" onClick={() => setShowNovaCampanha(true)} data-testid="btn-nova-campanha-ura"><Plus className="h-4 w-4" /> Nova Campanha</Button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4" data-testid="camp-view-toggle">
                <button onClick={() => setCampView("ativas")} className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${campView === "ativas" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="camp-view-ativas">Ativas ({campanhas.filter(c => !c.arquivada).length})</button>
                <button onClick={() => setCampView("arquivadas")} className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors flex items-center gap-1 ${campView === "arquivadas" ? "bg-slate-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="camp-view-arquivadas"><Archive className="h-3 w-3" /> Arquivadas ({campanhas.filter(c => c.arquivada).length})</button>
              </div>
              {(() => {
                const visiveis = campanhas.filter(c => campView === "arquivadas" ? c.arquivada : !c.arquivada);
                return visiveis.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Megaphone className="h-10 w-10 mx-auto mb-2 opacity-20" />
                  <p className="font-medium">{campView === "arquivadas" ? "Nenhuma campanha arquivada" : "Nenhuma campanha"}</p>
                  <p className="text-sm">{campView === "arquivadas" ? "Campanhas arquivadas aparecem aqui" : "Crie campanhas para iniciar discagem automatica"}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Audio</TableHead>
                        <TableHead className="text-center">Contatos</TableHead>
                        <TableHead className="text-center">Atendidas</TableHead>
                        <TableHead className="text-center">N/Atendida</TableHead>
                        <TableHead className="text-center">Ocupados</TableHead>
                        <TableHead className="text-center">Pendente</TableHead>
                        <TableHead className="text-center">Erro</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tempo</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Acoes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visiveis.map(c => {
                        const realizadas = c.chamadas_realizadas || 0;
                        const erros = (c.chamadas_nao_atendidas || 0);
                        const total = c.total_contatos || 0;
                        const processados = realizadas;  // realizadas ja inclui nao-atendidas (evita dupla contagem >100%)
                        return (
                        <TableRow key={c.id} data-testid={`campanha-ura-row-${c.id}`}>
                          <TableCell className="font-medium text-sm">{c.nome}</TableCell>
                          <TableCell className="text-xs text-gray-600">{c.audio_nome || "-"}</TableCell>
                          <TableCell className="text-center">{total}</TableCell>
                          <TableCell className="text-center font-medium text-green-600">{c.chamadas_atendidas || 0}</TableCell>
                          <TableCell className="text-center font-medium text-amber-600">{erros}</TableCell>
                          <TableCell className="text-center font-medium text-orange-500">{c.chamadas_ocupadas || 0}</TableCell>
                          <TableCell className="text-center font-medium text-blue-600">{c.chamadas_pendentes != null ? c.chamadas_pendentes : Math.max(total - (c.chamadas_atendidas || 0) - erros - (c.chamadas_ocupadas || 0) - (c.chamadas_erro || 0), 0)}</TableCell>
                          <TableCell className="text-center font-medium text-red-600">{c.chamadas_erro || 0}</TableCell>
                          <TableCell>
                            <Badge className={
                              c.status === "FINALIZADA" ? "bg-green-100 text-green-700" :
                              c.status === "EM_ANDAMENTO" ? "bg-blue-100 text-blue-700 animate-pulse" :
                              c.status === "PAUSADA" ? "bg-orange-100 text-orange-700" :
                              c.status === "ERRO" ? "bg-red-100 text-red-700" :
                              c.status === "RASCUNHO" ? "bg-yellow-100 text-yellow-700" :
                              "bg-gray-100 text-gray-700"
                            }>{c.status === "EM_ANDAMENTO" ? "DISCANDO..." : c.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {(() => {
                              if (c.status === "FINALIZADA" && c.data_inicio && c.data_fim) {
                                const duracao = (new Date(c.data_fim) - new Date(c.data_inicio)) / 1000;
                                if (duracao < 60) return <span className="text-green-600 font-medium">{Math.round(duracao)}s</span>;
                                if (duracao < 3600) return <span className="text-green-600 font-medium">{Math.round(duracao / 60)}min</span>;
                                return <span className="text-green-600 font-medium">{Math.floor(duracao / 3600)}h {Math.round((duracao % 3600) / 60)}min</span>;
                              }
                              if (c.status === "EM_ANDAMENTO" && c.data_inicio && processados > 0) {
                                const elapsed = (Date.now() - new Date(c.data_inicio).getTime()) / 1000;
                                const rate = processados / elapsed;
                                const restante = total - processados;
                                const eta = rate > 0 ? restante / rate : 0;
                                const pct = Math.round(processados / Math.max(total, 1) * 100);
                                let etaText = "";
                                if (eta < 60) etaText = `~${Math.round(eta)}s`;
                                else if (eta < 3600) etaText = `~${Math.round(eta / 60)}min`;
                                else etaText = `~${Math.floor(eta / 3600)}h ${Math.round((eta % 3600) / 60)}m`;
                                return (
                                  <div className="space-y-1">
                                    <span className="text-blue-600 font-medium text-xs">{etaText} restante</span>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-[10px] text-gray-400">{pct}% · {(rate * 60).toFixed(0)} ch/min</span>
                                  </div>
                                );
                              }
                              if (c.status === "PAUSADA" && processados > 0) {
                                const pct = Math.round(processados / Math.max(total, 1) * 100);
                                return <span className="text-orange-500 text-xs">{pct}% pausado</span>;
                              }
                              return <span className="text-gray-400 text-xs">-</span>;
                            })()}
                          </TableCell>
                          <TableCell className="text-sm">{fmt(c.data_inicio || c.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 flex-wrap">
                              {c.arquivada ? (
                                <>
                                  <Button size="sm" className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={() => desarquivarCampanha(c.id)} data-testid={`btn-restaurar-${c.id}`}>
                                    <RotateCcw className="h-3 w-3" /> Restaurar
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowReportCamp(c)} data-testid={`btn-relatorio-arq-${c.id}`}>
                                    <BarChart3 className="h-3 w-3" /> Relatorio
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluirCampanha(c.id)} data-testid={`btn-excluir-arq-${c.id}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </>
                              ) : (<>
                              {c.status === "RASCUNHO" && (
                                <Button size="sm" className="h-7 text-xs gap-1 bg-purple-600 hover:bg-purple-700" onClick={() => iniciarCampanha(c.id)} data-testid={`btn-iniciar-${c.id}`}>
                                  <Rocket className="h-3 w-3" /> Iniciar
                                </Button>
                              )}
                              {c.status === "EM_ANDAMENTO" && (
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => pausarCampanha(c.id)}>
                                  <Pause className="h-3 w-3" /> Pausar
                                </Button>
                              )}
                              {c.status === "PAUSADA" && (
                                <>
                                  <Button size="sm" className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={() => retomarDeOndeParou(c.id)} data-testid={`btn-retomar-${c.id}`}>
                                    <Play className="h-3 w-3" /> Retomar
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-green-300 text-green-600 hover:bg-green-50" onClick={() => finalizarCampanha(c.id)}>
                                    <CheckCircle className="h-3 w-3" /> Finalizar
                                  </Button>
                                </>
                              )}
                              {c.status === "FINALIZADA" && (
                                <>
                                  <Button size="sm" className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={() => reenviarCampanha(c.id)} data-testid={`btn-reenviar-${c.id}`}>
                                    <RefreshCw className="h-3 w-3" /> Reenviar
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-green-300 text-green-600 hover:bg-green-50" onClick={() => retomarDeOndeParou(c.id)} data-testid={`btn-retomar-final-${c.id}`}>
                                    <Play className="h-3 w-3" /> Retomar
                                  </Button>
                                </>
                              )}
                              {c.status === "ERRO" && (
                                <>
                                  <Button size="sm" className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={() => reenviarCampanha(c.id)}>
                                    <RefreshCw className="h-3 w-3" /> Reenviar
                                  </Button>
                                </>
                              )}
                              {c.status !== "EM_ANDAMENTO" && (
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => abrirEditarCampanha(c)} data-testid={`btn-editar-${c.id}`}>
                                  <Edit className="h-3 w-3" /> Editar
                                </Button>
                              )}
                              {c.status !== "RASCUNHO" && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-indigo-300 text-indigo-600 hover:bg-indigo-50" data-testid={`btn-rediscar-${c.id}`}>
                                      <RefreshCw className="h-3 w-3" /> Rediscar
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => retomarDeOndeParou(c.id)} data-testid={`rediscar-pendentes-${c.id}`}>Rediscar Pendentes (De Onde Parou) ({c.chamadas_pendentes || 0})</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => rediscarGrupo(c.id, "ATENDIDA", "Atendidas")} data-testid={`rediscar-atendidas-${c.id}`}>Rediscar Atendidas ({c.chamadas_atendidas || 0})</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => rediscarGrupo(c.id, "NAO_ATENDIDA", "N/Atendida")} data-testid={`rediscar-natendida-${c.id}`}>Rediscar N/Atendida ({c.chamadas_nao_atendidas || 0})</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => rediscarGrupo(c.id, "OCUPADO", "Ocupados/Rejeitados")} data-testid={`rediscar-ocupados-${c.id}`}>Rediscar Ocupados/Rejeitados ({c.chamadas_ocupadas || 0})</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => rediscarGrupo(c.id, "ERRO", "Erro")} data-testid={`rediscar-erro-${c.id}`}>Rediscar Erro ({c.chamadas_erro || 0})</DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              <Button size="sm" variant="outline" onClick={() => { setAdicionarContatosModal({id: c.id, nome: c.nome, grupo_contato: c.grupo_contato || ""}); setAdicionarForm({ tipo_telefone: "todos", whatsapp_status: "todos", sms_status: "todos", acao: "todos", limite_contatos: 0 }); }} className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`btn-mais-contatos-${c.id}`}>
                                <Plus className="h-3 w-3" /> Mais
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowReportCamp(c)} data-testid={`btn-relatorio-${c.id}`}>
                                <BarChart3 className="h-3 w-3" /> Relatorio
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluirCampanha(c.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-slate-300 text-slate-600 hover:bg-slate-100" onClick={() => arquivarCampanha(c.id)} data-testid={`btn-arquivar-${c.id}`}>
                                <Archive className="h-3 w-3" /> Arquivar
                              </Button>
                              </>)}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Audios === */}
        <TabsContent value="audios" className="space-y-4" data-testid="tab-audios">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="flex items-center gap-2"><Music className="h-5 w-5 text-green-600" /> Audios</CardTitle>
                <CardDescription>Mensagens gravadas para reproducao na URA</CardDescription>
              </div>
              <Button className="gap-1 bg-green-600 hover:bg-green-700" onClick={() => setShowUploadAudio(true)} data-testid="btn-upload-audio"><Upload className="h-4 w-4" /> Enviar Audio</Button>
            </CardHeader>
            <CardContent>
              {audios.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground"><FileAudio className="h-10 w-10 mx-auto mb-2 opacity-20" /><p className="font-medium">Nenhum audio</p><p className="text-sm">Envie audios .mp3, .wav ou .ogg para usar nas campanhas</p></div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {audios.map(a => (
                    <Card key={a.id} className="bg-white" data-testid={`audio-card-${a.id}`}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold text-sm">{a.nome}</h4>
                            {a.descricao && <p className="text-xs text-gray-500 mt-0.5">{a.descricao}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-teal-600" onClick={() => enviarIVRGateway(a.id)} title="Enviar ao DINSTAR">
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluirAudio(a.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{a.formato?.toUpperCase()}</Badge>
                          <span className="text-xs text-gray-400">{(a.tamanho / 1024).toFixed(0)} KB</span>
                          <span className="text-xs text-gray-400">{fmt(a.created_at)}</span>
                        </div>
                        <audio controls className="w-full mt-2 h-8" src={`${API}/voip/audios/${a.id}/play`} preload="none" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Chamadas === */}
        <TabsContent value="chamadas" className="space-y-4" data-testid="tab-chamadas">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="flex items-center gap-2"><PhoneCall className="h-5 w-5 text-orange-600" /> Historico de Chamadas</CardTitle>
                <CardDescription>{chamadas.length} chamada(s)</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="gap-1" onClick={carregarTudo}><RefreshCw className="h-4 w-4" /> Atualizar</Button>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-gray-600 whitespace-nowrap">Campanha:</Label>
                  <Select value={filtroCampChamada} onValueChange={setFiltroCampChamada}>
                    <SelectTrigger className="w-[180px] h-8 bg-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas</SelectItem>
                      {campanhas.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-gray-600 whitespace-nowrap">Status:</Label>
                  <Select value={filtroStatusChamada} onValueChange={setFiltroStatusChamada}>
                    <SelectTrigger className="w-[150px] h-8 bg-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {["PENDENTE", "DISCANDO", "ATENDIDA", "NAO_ATENDIDA", "FINALIZADA", "TRANSFERIDA", "ERRO", "AGUARDANDO_INFRA"].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {chamadasFiltradas.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground"><PhoneOff className="h-8 w-8 mx-auto mb-2 opacity-20" /><p className="text-sm">Nenhuma chamada registrada</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Numero</TableHead><TableHead>Contato</TableHead><TableHead>Campanha</TableHead>
                        <TableHead>Status</TableHead><TableHead>DTMF</TableHead><TableHead>Qualif.</TableHead>
                        <TableHead>Duracao</TableHead><TableHead>Tent.</TableHead><TableHead>Data</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {chamadasFiltradas.slice(0, 100).map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono text-xs">{c.numero}</TableCell>
                          <TableCell className="text-xs">{c.nome_contato || "-"}</TableCell>
                          <TableCell className="text-xs">{c.campanha_nome || "-"}</TableCell>
                          <TableCell><Badge className={`text-xs ${statusColors[c.status] || "bg-gray-100"}`}>{c.status}</Badge></TableCell>
                          <TableCell className="text-center">{c.dtmf_pressionado ? <Badge className="bg-purple-100 text-purple-700 text-xs">Tecla {c.dtmf_pressionado}</Badge> : "-"}</TableCell>
                          <TableCell className="text-center">{c.qualificado ? <CheckCircle className="h-4 w-4 text-green-600 mx-auto" /> : <XCircle className="h-4 w-4 text-gray-300 mx-auto" />}</TableCell>
                          <TableCell className="text-xs">{fmtDur(c.duracao)}</TableCell>
                          <TableCell className="text-center text-xs">{c.tentativas || 0}</TableCell>
                          <TableCell className="text-xs text-gray-500">{fmt(c.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Receptivo === */}
        <TabsContent value="receptivo" className="space-y-4" data-testid="tab-receptivo">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Headphones className="h-5 w-5 text-teal-600" /> Central de Receptivo</CardTitle>
                <CardDescription>Configure o atendimento de chamadas recebidas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {receptivoConfig && (
                  <>
                    <div className="flex items-center justify-between">
                      <Label>Receptivo Ativo</Label>
                      <Button
                        size="sm" variant={receptivoConfig.ativo ? "default" : "outline"}
                        className={receptivoConfig.ativo ? "bg-green-600 hover:bg-green-700" : ""}
                        onClick={() => setReceptivoConfig({ ...receptivoConfig, ativo: !receptivoConfig.ativo })}
                        data-testid="btn-toggle-receptivo"
                      >
                        {receptivoConfig.ativo ? "Ativo" : "Inativo"}
                      </Button>
                    </div>
                    <div>
                      <Label>Mensagem de Boas-vindas</Label>
                      <Input value={receptivoConfig.mensagem_boas_vindas || ""} onChange={e => setReceptivoConfig({ ...receptivoConfig, mensagem_boas_vindas: e.target.value })} />
                    </div>
                    <div>
                      <Label>Audio da URA</Label>
                      <Select value={receptivoConfig.audio_ura_id || ""} onValueChange={v => setReceptivoConfig({ ...receptivoConfig, audio_ura_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecionar audio" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value=" ">Nenhum</SelectItem>
                          {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Horario Inicio</Label><Input type="time" value={receptivoConfig.horario_atendimento?.inicio || "08:00"} onChange={e => setReceptivoConfig({ ...receptivoConfig, horario_atendimento: { ...receptivoConfig.horario_atendimento, inicio: e.target.value } })} /></div>
                      <div><Label>Horario Fim</Label><Input type="time" value={receptivoConfig.horario_atendimento?.fim || "18:00"} onChange={e => setReceptivoConfig({ ...receptivoConfig, horario_atendimento: { ...receptivoConfig.horario_atendimento, fim: e.target.value } })} /></div>
                    </div>
                    <div>
                      <Label>Mensagem Fora de Horario</Label>
                      <Input value={receptivoConfig.mensagem_fora_horario || ""} onChange={e => setReceptivoConfig({ ...receptivoConfig, mensagem_fora_horario: e.target.value })} />
                    </div>
                    <div>
                      <Label>Audio Fora de Horario</Label>
                      <Select value={receptivoConfig.audio_fora_horario_id || ""} onValueChange={v => setReceptivoConfig({ ...receptivoConfig, audio_fora_horario_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecione o audio" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value=" ">Nenhum</SelectItem>
                          {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Separator />
                    <div>
                      <Label className="font-semibold text-orange-600">Receptivo Campanha</Label>
                      <p className="text-xs text-gray-500 mb-2">Audio especifico tocado quando o cliente vem de uma campanha VoIP</p>
                      <Select value={receptivoConfig.audio_campanha_id || ""} onValueChange={v => setReceptivoConfig({ ...receptivoConfig, audio_campanha_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Selecione o audio da campanha" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value=" ">Nenhum (usa audio padrao)</SelectItem>
                          {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <Label className="font-semibold">Filas de Atendimento</Label>
                          <p className="text-xs text-gray-500">Configure as opcoes da URA receptiva</p>
                        </div>
                        <Button size="sm" variant="outline" className="gap-1 text-teal-600 border-teal-300" onClick={() => {
                          const nf = [...(receptivoConfig.filas || [])];
                          const usedKeys = nf.map(f => f.tecla);
                          const nextKey = ["1","2","3","4","5","6","7","8","9","0"].find(k => !usedKeys.includes(k)) || "0";
                          nf.push({ tecla: nextKey, nome: "", tipo: "transferir", ramal: "", audio_id: "", sub_opcoes: [], coletar_cpf: false, audio_cpf_id: "", audio_confirmacao_id: "", enviar_sms: false, enviar_whatsapp: false, transferir_apos: false, ramal_apos: "" });
                          setReceptivoConfig({ ...receptivoConfig, filas: nf });
                        }} data-testid="btn-add-fila">
                          <Plus className="h-3 w-3" /> Fila
                        </Button>
                      </div>
                      {(receptivoConfig.filas || []).map((fila, idx) => (
                        <div key={idx} className="mb-3 p-3 rounded-lg border bg-gray-50 space-y-2">
                          <div className="flex gap-2 items-center">
                            <Badge className="bg-teal-100 text-teal-700 shrink-0">Tecla {fila.tecla}</Badge>
                            <Input className="flex-1 h-8" value={fila.nome} onChange={e => {
                              const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], nome: e.target.value };
                              setReceptivoConfig({ ...receptivoConfig, filas: nf });
                            }} placeholder="Nome da fila" />
                            <Select value={fila.tipo || "transferir"} onValueChange={v => {
                              const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], tipo: v };
                              setReceptivoConfig({ ...receptivoConfig, filas: nf });
                            }}>
                              <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sub_menu">Sub-Menu</SelectItem>
                                <SelectItem value="transferir">Transferir</SelectItem>
                                <SelectItem value="blacklist">Blacklist</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => {
                              const nf = receptivoConfig.filas.filter((_, i) => i !== idx);
                              setReceptivoConfig({ ...receptivoConfig, filas: nf });
                            }}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>

                          {/* Audio da fila */}
                          <div className="ml-6">
                            <Label className="text-xs text-gray-500">Audio desta fila</Label>
                            <Select value={fila.audio_id || ""} onValueChange={v => {
                              const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], audio_id: v };
                              setReceptivoConfig({ ...receptivoConfig, filas: nf });
                            }}>
                              <SelectTrigger className="h-8"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value=" ">Nenhum</SelectItem>
                                {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Transferir - Ramal */}
                          {fila.tipo === "transferir" && (
                            <div className="ml-6">
                              <Label className="text-xs text-gray-500">Ramal</Label>
                              <Input className="h-8" value={fila.ramal || ""} onChange={e => {
                                const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], ramal: e.target.value };
                                setReceptivoConfig({ ...receptivoConfig, filas: nf });
                              }} placeholder="Numero do ramal" />
                            </div>
                          )}

                          {/* Sub-Menu */}
                          {fila.tipo === "sub_menu" && (
                            <div className="ml-6 space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs font-semibold text-teal-700">Sub-opcoes</Label>
                                <Button size="sm" variant="ghost" className="h-6 text-xs text-teal-600 gap-1 px-2" onClick={() => {
                                  const nf = [...receptivoConfig.filas];
                                  const subs = [...(nf[idx].sub_opcoes || [])];
                                  const usedSubKeys = subs.map(s => s.tecla);
                                  const allKeys = ["1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","16","17","18","19","20","0"];
                                  const nextSub = allKeys.find(k => !usedSubKeys.includes(k)) || String(subs.length + 1);
                                  subs.push({ tecla: nextSub, nome: "", mensagem_wpp: "", audio_id: "", coletar_cpf: false, audio_cpf_id: "", audio_confirmacao_id: "", enviar_sms: false, enviar_whatsapp: false, sms_template_id: "", wpp_flow_id: "", transferir_apos: false, ramal_apos: "" });
                                  nf[idx] = { ...nf[idx], sub_opcoes: subs };
                                  setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                }}>
                                  <Plus className="h-3 w-3" /> Opcao
                                </Button>
                              </div>
                              {(fila.sub_opcoes || []).map((sub, si) => (
                                <div key={si} className="p-2 rounded border bg-white space-y-1">
                                  <div className="flex gap-2 items-center">
                                    <div className="flex items-center gap-1 shrink-0">
                                      <span className="text-xs text-gray-500">Tecla</span>
                                      <Input className="w-12 h-7 text-xs text-center px-1" value={sub.tecla} onChange={e => {
                                        const nf = [...receptivoConfig.filas];
                                        const subs = [...nf[idx].sub_opcoes]; subs[si] = { ...subs[si], tecla: e.target.value };
                                        nf[idx] = { ...nf[idx], sub_opcoes: subs };
                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                      }} />
                                    </div>
                                    <Input className="flex-1 h-7 text-sm" value={sub.nome} onChange={e => {
                                      const nf = [...receptivoConfig.filas];
                                      const subs = [...nf[idx].sub_opcoes]; subs[si] = { ...subs[si], nome: e.target.value };
                                      nf[idx] = { ...nf[idx], sub_opcoes: subs };
                                      setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                    }} placeholder="Ex: INSS, Federal, Empresa Privada..." />
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => {
                                      const nf = [...receptivoConfig.filas];
                                      nf[idx] = { ...nf[idx], sub_opcoes: nf[idx].sub_opcoes.filter((_, i) => i !== si) };
                                      setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                    }}><X className="h-3 w-3" /></Button>
                                  </div>
                                  {/* === FLUXO INDEPENDENTE POR SUB-OPCAO === */}
                                  <div className="space-y-2 pt-2 border-t border-gray-200 mt-2">
                                    <Label className="text-xs font-semibold text-gray-600">Fluxo apos escolher segmento:</Label>
                                    {/* Step 1 - Collect CPF */}
                                    <div className="flex items-start gap-2 p-2 rounded bg-blue-50 border border-blue-100">
                                      <Badge className="bg-blue-600 text-white shrink-0 text-xs mt-0.5">1</Badge>
                                      <div className="flex-1 space-y-1">
                                        <label className="flex items-center gap-1.5 text-xs font-medium">
                                          <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={sub.coletar_cpf || false} onChange={e => {
                                            const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], coletar_cpf: e.target.checked };
                                            nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                          }} /> Coletar CPF do cliente
                                        </label>
                                        {sub.coletar_cpf && (
                                          <div>
                                            <Label className="text-xs text-gray-400">Audio pedindo CPF</Label>
                                            <Select value={sub.audio_cpf_id || ""} onValueChange={v => {
                                              const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], audio_cpf_id: v };
                                              nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                            }}>
                                              <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value=" ">Nenhum</SelectItem>
                                                {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {/* Step 2 - Audio do segmento */}
                                    <div className="flex items-start gap-2 p-2 rounded bg-purple-50 border border-purple-100">
                                      <Badge className="bg-purple-600 text-white shrink-0 text-xs mt-0.5">2</Badge>
                                      <div className="flex-1 space-y-1">
                                        <span className="text-xs font-medium">Audio do segmento (toca apos CPF)</span>
                                        <Select value={sub.audio_id || ""} onValueChange={v => {
                                          const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], audio_id: v };
                                          nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                        }}>
                                          <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value=" ">Nenhum</SelectItem>
                                            {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                    {/* Step 3 - Confirmation audio + send message */}
                                    <div className="flex items-start gap-2 p-2 rounded bg-green-50 border border-green-100">
                                      <Badge className="bg-green-600 text-white shrink-0 text-xs mt-0.5">3</Badge>
                                      <div className="flex-1 space-y-1">
                                        <span className="text-xs font-medium">Audio de confirmacao + Enviar mensagem</span>
                                        <div>
                                          <Label className="text-xs text-gray-400">Audio confirmacao ("Sua solicitacao foi recebida")</Label>
                                          <Select value={sub.audio_confirmacao_id || ""} onValueChange={v => {
                                            const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], audio_confirmacao_id: v };
                                            nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                          }}>
                                            <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value=" ">Nenhum</SelectItem>
                                              {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="flex items-center gap-3 pt-1">
                                          <label className="flex items-center gap-1.5 text-xs">
                                            <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={sub.enviar_sms || false} onChange={e => {
                                              const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], enviar_sms: e.target.checked };
                                              nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                            }} /> Enviar SMS
                                          </label>
                                          <label className="flex items-center gap-1.5 text-xs">
                                            <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={sub.enviar_whatsapp || false} onChange={e => {
                                              const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], enviar_whatsapp: e.target.checked };
                                              nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                            }} /> Enviar WhatsApp
                                          </label>
                                        </div>
                                        {sub.enviar_sms && (
                                          <div>
                                            <Label className="text-xs text-gray-400">Template SMS</Label>
                                            <Select value={sub.sms_template_id || ""} onValueChange={v => {
                                              const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], sms_template_id: v };
                                              nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                            }}>
                                              <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Selecione template SMS" /></SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value=" ">Usar mensagem da sub-opcao</SelectItem>
                                                {smsTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        {sub.enviar_whatsapp && (
                                          <div>
                                            <Label className="text-xs text-gray-400">Fluxo WhatsApp</Label>
                                            <Select value={sub.wpp_flow_id || ""} onValueChange={v => {
                                              const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], wpp_flow_id: v };
                                              nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                            }}>
                                              <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Selecione fluxo WhatsApp" /></SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value=" ">Usar mensagem da sub-opcao</SelectItem>
                                                {wppFlows.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        {/* Screen Pop Template por Segmento */}
                                        {(sub.enviar_sms || sub.enviar_whatsapp) && (
                                          <div className="mt-2 p-2 rounded border border-cyan-200 bg-cyan-50 space-y-2">
                                            <label className="flex items-center gap-1.5 text-xs font-semibold text-cyan-700">
                                              <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={sub.sp_ativo || false} onChange={e => {
                                                const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], sp_ativo: e.target.checked };
                                                nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                              }} /> Screen Pop (Landing Page com Link)
                                            </label>
                                            {sub.sp_ativo && (
                                              <div className="space-y-1.5">
                                                <div className="grid grid-cols-2 gap-1.5">
                                                  <div>
                                                    <Label className="text-xs text-gray-500">Titulo</Label>
                                                    <Input className="h-7 text-xs" value={sub.sp_titulo || ""} placeholder="Ex: Simulacao INSS" onChange={e => {
                                                      const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], sp_titulo: e.target.value };
                                                      nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                    }} />
                                                  </div>
                                                  <div>
                                                    <Label className="text-xs text-gray-500">Empresa</Label>
                                                    <Input className="h-7 text-xs" value={sub.sp_empresa || "StiloCred"} onChange={e => {
                                                      const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], sp_empresa: e.target.value };
                                                      nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                    }} />
                                                  </div>
                                                </div>
                                                <div>
                                                  <Label className="text-xs text-gray-500">Mensagem da Pagina</Label>
                                                  <Input className="h-7 text-xs" value={sub.sp_mensagem || ""} placeholder="Temos uma oferta especial..." onChange={e => {
                                                    const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], sp_mensagem: e.target.value };
                                                    nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                  }} />
                                                </div>
                                                {sub.enviar_sms && (
                                                  <div>
                                                    <Label className="text-xs text-gray-500">Texto SMS (use {"{link}"} para o link)</Label>
                                                    <Input className="h-7 text-xs" value={sub.sp_sms_msg || ""} placeholder="StiloCred: Acesse {link}" onChange={e => {
                                                      const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], sp_sms_msg: e.target.value };
                                                      nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                    }} />
                                                  </div>
                                                )}
                                                {sub.enviar_whatsapp && (
                                                  <div>
                                                    <Label className="text-xs text-gray-500">Texto WhatsApp (use {"{link}"} para o link)</Label>
                                                    <Input className="h-7 text-xs" value={sub.sp_wpp_msg || ""} placeholder="Ola! Acesse: {link}" onChange={e => {
                                                      const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], sp_wpp_msg: e.target.value };
                                                      nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                    }} />
                                                  </div>
                                                )}
                                                <div>
                                                  <Label className="text-xs text-gray-500">Botoes da Landing Page</Label>
                                                  {(sub.sp_botoes || []).map((btn, bi) => (
                                                    <div key={bi} className="flex gap-1 items-center mb-1">
                                                      <Input className="flex-1 h-6 text-xs" value={btn.label} placeholder="Texto do botao" onChange={e => {
                                                        const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes];
                                                        const nb = [...(subs2[si].sp_botoes || [])]; nb[bi] = { ...nb[bi], label: e.target.value };
                                                        subs2[si] = { ...subs2[si], sp_botoes: nb }; nf[idx] = { ...nf[idx], sub_opcoes: subs2 };
                                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                      }} />
                                                      <select className="h-6 text-xs rounded border px-1" value={btn.estilo || "primary"} onChange={e => {
                                                        const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes];
                                                        const nb = [...(subs2[si].sp_botoes || [])]; nb[bi] = { ...nb[bi], estilo: e.target.value };
                                                        subs2[si] = { ...subs2[si], sp_botoes: nb }; nf[idx] = { ...nf[idx], sub_opcoes: subs2 };
                                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                      }}>
                                                        <option value="primary">Destaque</option>
                                                        <option value="secondary">Normal</option>
                                                      </select>
                                                      <button className="text-red-400 text-xs px-1" onClick={() => {
                                                        const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes];
                                                        const nb = (subs2[si].sp_botoes || []).filter((_, x) => x !== bi);
                                                        subs2[si] = { ...subs2[si], sp_botoes: nb }; nf[idx] = { ...nf[idx], sub_opcoes: subs2 };
                                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                      }}>x</button>
                                                    </div>
                                                  ))}
                                                  <button className="text-xs text-cyan-600 mt-1" onClick={() => {
                                                    const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes];
                                                    const nb = [...(subs2[si].sp_botoes || []), { label: "", acao: "custom", estilo: "primary" }];
                                                    subs2[si] = { ...subs2[si], sp_botoes: nb }; nf[idx] = { ...nf[idx], sub_opcoes: subs2 };
                                                    setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                                  }}>+ Adicionar Botao</button>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {/* Step 4 - Transfer */}
                                    <div className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-100">
                                      <Badge className="bg-amber-600 text-white shrink-0 text-xs mt-0.5">4</Badge>
                                      <div className="flex-1 space-y-1">
                                        <label className="flex items-center gap-1.5 text-xs font-medium">
                                          <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={sub.transferir_apos || false} onChange={e => {
                                            const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], transferir_apos: e.target.checked };
                                            nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                          }} /> Transferir para ramal apos finalizar
                                        </label>
                                        {sub.transferir_apos && (
                                          <Input className="h-7 text-sm" value={sub.ramal_apos || ""} onChange={e => {
                                            const nf = [...receptivoConfig.filas]; const subs2 = [...nf[idx].sub_opcoes]; subs2[si] = { ...subs2[si], ramal_apos: e.target.value };
                                            nf[idx] = { ...nf[idx], sub_opcoes: subs2 }; setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                          }} placeholder="Numero do ramal" />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* CPF Collection + SMS/WhatsApp + Transfer after - only for non-sub_menu types */}
                          {fila.tipo !== "blacklist" && fila.tipo !== "sub_menu" && (
                            <div className="ml-6 space-y-3 pt-2 border-t border-gray-200">
                              <Label className="text-xs font-semibold text-gray-600">Fluxo apos escolher segmento:</Label>
                              {/* Step 1 - Collect CPF */}
                              <div className="flex items-start gap-2 p-2 rounded bg-blue-50 border border-blue-100">
                                <Badge className="bg-blue-600 text-white shrink-0 text-xs mt-0.5">1</Badge>
                                <div className="flex-1 space-y-1">
                                  <label className="flex items-center gap-1.5 text-xs font-medium">
                                    <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={fila.coletar_cpf || false} onChange={e => {
                                      const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], coletar_cpf: e.target.checked };
                                      setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                    }} /> Coletar CPF do cliente
                                  </label>
                                  {fila.coletar_cpf && (
                                    <div>
                                      <Label className="text-xs text-gray-400">Audio pedindo CPF</Label>
                                      <Select value={fila.audio_cpf_id || ""} onValueChange={v => {
                                        const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], audio_cpf_id: v };
                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                      }}>
                                        <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value=" ">Nenhum</SelectItem>
                                          {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Step 2 - Audio do segmento */}
                              <div className="flex items-start gap-2 p-2 rounded bg-purple-50 border border-purple-100">
                                <Badge className="bg-purple-600 text-white shrink-0 text-xs mt-0.5">2</Badge>
                                <div className="flex-1 space-y-1">
                                  <span className="text-xs font-medium">Audio do segmento (toca apos CPF)</span>
                                  <div>
                                    <Label className="text-xs text-gray-400">Audio padrao do segmento</Label>
                                    <Select value={fila.audio_segmento_id || ""} onValueChange={v => {
                                      const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], audio_segmento_id: v };
                                      setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                    }}>
                                      <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value=" ">Nenhum</SelectItem>
                                        {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <p className="text-xs text-gray-400">Cada sub-opcao pode ter audio proprio (configurado acima)</p>
                                </div>
                              </div>
                              {/* Step 3 - Confirmation audio + send message */}
                              <div className="flex items-start gap-2 p-2 rounded bg-green-50 border border-green-100">
                                <Badge className="bg-green-600 text-white shrink-0 text-xs mt-0.5">3</Badge>
                                <div className="flex-1 space-y-1">
                                  <span className="text-xs font-medium">Audio de confirmacao + Enviar mensagem</span>
                                  <div>
                                    <Label className="text-xs text-gray-400">Audio confirmacao ("Sua solicitacao foi recebida")</Label>
                                    <Select value={fila.audio_confirmacao_id || ""} onValueChange={v => {
                                      const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], audio_confirmacao_id: v };
                                      setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                    }}>
                                      <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value=" ">Nenhum</SelectItem>
                                        {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="flex items-center gap-3 pt-1">
                                    <label className="flex items-center gap-1.5 text-xs">
                                      <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={fila.enviar_sms || false} onChange={e => {
                                        const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], enviar_sms: e.target.checked };
                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                      }} /> Enviar SMS
                                    </label>
                                    <label className="flex items-center gap-1.5 text-xs">
                                      <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={fila.enviar_whatsapp || false} onChange={e => {
                                        const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], enviar_whatsapp: e.target.checked };
                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                      }} /> Enviar WhatsApp
                                    </label>
                                  </div>
                                  {fila.enviar_sms && (
                                    <div>
                                      <Label className="text-xs text-gray-400">Template SMS</Label>
                                      <Select value={fila.sms_template_id || ""} onValueChange={v => {
                                        const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], sms_template_id: v };
                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                      }}>
                                        <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Selecione template SMS" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value=" ">Usar mensagem da sub-opcao</SelectItem>
                                          {smsTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                  {fila.enviar_whatsapp && (
                                    <div>
                                      <Label className="text-xs text-gray-400">Fluxo WhatsApp</Label>
                                      <Select value={fila.wpp_flow_id || ""} onValueChange={v => {
                                        const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], wpp_flow_id: v };
                                        setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                      }}>
                                        <SelectTrigger className="h-7 text-sm"><SelectValue placeholder="Selecione fluxo WhatsApp" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value=" ">Usar mensagem da sub-opcao</SelectItem>
                                          {wppFlows.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Step 4 - Transfer */}
                              <div className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-100">
                                <Badge className="bg-amber-600 text-white shrink-0 text-xs mt-0.5">4</Badge>
                                <div className="flex-1 space-y-1">
                                  <label className="flex items-center gap-1.5 text-xs font-medium">
                                    <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={fila.transferir_apos || false} onChange={e => {
                                      const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], transferir_apos: e.target.checked };
                                      setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                    }} /> Transferir para ramal apos finalizar
                                  </label>
                                  {fila.transferir_apos && (
                                    <Input className="h-7 text-sm" value={fila.ramal_apos || ""} onChange={e => {
                                      const nf = [...receptivoConfig.filas]; nf[idx] = { ...nf[idx], ramal_apos: e.target.value };
                                      setReceptivoConfig({ ...receptivoConfig, filas: nf });
                                    }} placeholder="Numero do ramal" />
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={salvarReceptivo} data-testid="btn-salvar-receptivo">Salvar Configuracao</Button>
                  </>
                )}
                {!config.ami_configurado && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Asterisk AMI necessario
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      O receptivo precisa do Asterisk para funcionar. Configure o AMI nas configuracoes.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><PhoneIncoming className="h-5 w-5 text-teal-600" /> Chamadas Recebidas</CardTitle>
                <CardDescription>Historico de chamadas do receptivo</CardDescription>
              </CardHeader>
              <CardContent>
                {receptivoChamadas.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <PhoneIncoming className="h-8 w-8 mx-auto mb-2 opacity-20" />
                    <p className="text-sm">Nenhuma chamada recebida</p>
                    <p className="text-xs text-gray-400 mt-1">As chamadas aparecerao aqui quando o receptivo estiver ativo</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {receptivoChamadas.map(c => (
                      <div key={c.id} className="p-2 rounded border bg-white flex items-center justify-between">
                        <div>
                          <p className="font-mono text-sm">{c.numero_origem}</p>
                          <p className="text-xs text-gray-500">{fmt(c.created_at)}</p>
                        </div>
                        <div className="flex gap-2 items-center">
                          {c.dtmf_pressionado && <Badge className="bg-teal-100 text-teal-700">Tecla {c.dtmf_pressionado}</Badge>}
                          <Badge variant="outline">{c.fila_destino || c.status}</Badge>
                          <span className="text-xs text-gray-500">{fmtDur(c.duracao)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        </TabsContent>

        {/* === Relatorios === */}
        <TabsContent value="relatorios" className="space-y-4" data-testid="tab-relatorios-voip">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-slate-600" /> Relatorio VoIP</CardTitle></CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 mb-6">
                {[
                  { Icon: PhoneCall, color: "blue", val: relatorio.total_chamadas || 0, label: "Total Chamadas" },
                  { Icon: PhoneIncoming, color: "green", val: relatorio.chamadas_atendidas || 0, label: `Atendidas (${relatorio.taxa_atendimento || 0}%)` },
                  { Icon: CheckCircle, color: "purple", val: relatorio.chamadas_qualificadas || 0, label: `Qualificadas (${relatorio.taxa_qualificacao || 0}%)` },
                  { Icon: Clock, color: "orange", val: `${relatorio.duracao_total_minutos || 0}min`, label: `Duracao (Media: ${relatorio.duracao_media_segundos || 0}s)` },
                ].map((item, i) => (
                  <div key={i} className={`p-4 rounded-lg bg-${item.color}-50 border border-${item.color}-200 text-center`}>
                    <item.Icon className={`h-6 w-6 text-${item.color}-600 mx-auto mb-2`} />
                    <p className={`text-2xl font-bold text-${item.color}-700`}>{item.val}</p>
                    <p className={`text-xs text-${item.color}-600`}>{item.label}</p>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Megaphone className="h-4 w-4" /> Campanhas</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Total</span><span className="font-medium">{relatorio.total_campanhas || 0}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Ativas</span><span className="font-medium text-blue-600">{relatorio.campanhas_ativas || 0}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Finalizadas</span><span className="font-medium text-green-600">{relatorio.campanhas_finalizadas || 0}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Audios</span><span className="font-medium">{relatorio.audios_total || 0}</span></div>
                  </div>
                </div>
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Radio className="h-4 w-4" /> DTMF</h4>
                  {Object.keys(relatorio.dtmf_stats || {}).length === 0 ? (
                    <p className="text-sm text-gray-400">Nenhuma interacao DTMF</p>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(relatorio.dtmf_stats || {}).sort().map(([tecla, qtd]) => (
                        <div key={tecla} className="flex justify-between text-sm items-center">
                          <span className="text-gray-600">Tecla <Badge className="bg-purple-100 text-purple-700">{tecla}</Badge></span>
                          <span className="font-medium">{qtd}x</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Headphones className="h-4 w-4" /> Receptivo</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Total Recebidas</span><span className="font-medium">{relatorio.receptivo_total || 0}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Hoje</span><span className="font-medium text-teal-600">{relatorio.receptivo_hoje || 0}</span></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Solicitacoes da URA === */}
        <TabsContent value="solicitacoes" className="space-y-4" data-testid="tab-solicitacoes-ura">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-purple-600" /> Solicitacoes da URA</CardTitle>
                  <CardDescription>Dados coletados pela URA receptiva (segmento, CPF, telefone)</CardDescription>
                </div>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white shrink-0" onClick={rodarBackfillCadastro} disabled={backfillRodando} data-testid="btn-backfill-cadastro-ura">
                  {backfillRodando ? "Cadastrando..." : "Consultar + Cadastrar (Nova Vida)"}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 pt-3">
                {(() => {
                  const _st = (x) => (x.atendimento_status || "").toUpperCase();
                  const counts = {
                    TODOS: uraSolicitacoes.length,
                    ATIVOS: uraSolicitacoes.filter(x => _st(x) !== "ADIADO" && _st(x) !== "FINALIZADO").length,
                    ADIADO: uraSolicitacoes.filter(x => _st(x) === "ADIADO").length,
                    FINALIZADO: uraSolicitacoes.filter(x => _st(x) === "FINALIZADO").length,
                  };
                  const opts = [
                    { key: "TODOS", label: "Todos", active: "bg-slate-800 text-white" },
                    { key: "ATIVOS", label: "Ativos", active: "bg-blue-600 text-white" },
                    { key: "ADIADO", label: "Adiado", active: "bg-amber-500 text-white" },
                    { key: "FINALIZADO", label: "Finalizado", active: "bg-green-600 text-white" },
                  ];
                  return opts.map(o => (
                    <button key={o.key} data-testid={`ura-filtro-${o.key.toLowerCase()}`}
                      onClick={() => setUraFiltroAtend(o.key)}
                      className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${uraFiltroAtend === o.key ? o.active : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                      {o.label} <span className="opacity-70">({counts[o.key]})</span>
                    </button>
                  ));
                })()}
              </div>
            </CardHeader>
            <CardContent>
              {uraSolicitacoes.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">Nenhuma solicitacao recebida</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Origem</TableHead>
                        <TableHead data-testid="sol-campanha-col-marker">Campanha</TableHead>
                        <TableHead>Fila</TableHead>
                        <TableHead>Segmento</TableHead>
                        <TableHead>CPF</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tag Cliente</TableHead>
                        <TableHead>Cadastro / Nova Vida</TableHead>
                        <TableHead data-testid="sol-atendimento-col-marker">Atendimento</TableHead>
                        <TableHead data-testid="sol-ocorrencia-col-marker">Ocorrencia do atendente</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uraSolicitacoes.filter(s => {
                        const st = (s.atendimento_status || "").toUpperCase();
                        if (uraFiltroAtend === "ADIADO") return st === "ADIADO";
                        if (uraFiltroAtend === "FINALIZADO") return st === "FINALIZADO";
                        if (uraFiltroAtend === "ATIVOS") return st !== "ADIADO" && st !== "FINALIZADO";
                        return true;
                      }).map(s => (
                        <TableRow key={s.id}>
                          <TableCell className="text-xs">{s.data_hora ? new Date(s.data_hora).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "-"}</TableCell>
                          <TableCell className="font-mono text-xs">{s.telefone}</TableCell>
                          <TableCell><Badge className={s.origem === "campanha" ? "bg-orange-100 text-orange-700 text-xs" : s.origem === "fora_horario" ? "bg-red-100 text-red-700 text-xs" : "bg-blue-100 text-blue-700 text-xs"}>{s.origem === "campanha" ? "Campanha" : s.origem === "fora_horario" ? "Fora Horario" : "Receptivo"}</Badge></TableCell>
                          <TableCell className="text-xs">{s.campanha_nome || "-"}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{s.fila_nome || s.fila_tecla}</Badge></TableCell>
                          <TableCell><Badge className="bg-purple-100 text-purple-700 text-xs">{s.segmento || "-"}</Badge></TableCell>
                          <TableCell className="font-mono text-xs">{s.cpf || "-"}</TableCell>
                          <TableCell><Badge className="bg-green-100 text-green-700 text-xs">{s.status}</Badge></TableCell>
                          <TableCell>{(s.tags_cliente && s.tags_cliente.length > 0) ? (<div className="flex flex-wrap gap-1" data-testid={`sol-tags-${s.cpf || s.telefone}`}>{s.tags_cliente.map((tg, i) => (<Badge key={i} className="text-xs whitespace-nowrap border-0" style={{ color: tg.cor, backgroundColor: tg.bgCor }}>{tg.nome}</Badge>))}</div>) : (s.tag_cliente ? (<Badge className="bg-amber-100 text-amber-700 text-xs whitespace-nowrap">{s.tag_cliente}</Badge>) : (<span className="text-xs text-gray-300">-</span>))}</TableCell>
                          <TableCell data-testid={`sol-cadastro-${s.cpf || s.telefone}`}>
                            {s.cadastro_status === "CADASTRADO" ? (<div><Badge className="bg-emerald-100 text-emerald-700 text-xs">Cadastrado{s.nova_vida_consultado ? " + Nova Vida" : ""}</Badge>{s.cliente_nome ? <div className="text-[10px] text-gray-600 mt-0.5">{s.cliente_nome}</div> : null}{s.instituicao_vinculada ? <div className="text-[10px] text-gray-400">{s.instituicao_vinculada}</div> : null}</div>)
                            : s.cadastro_status === "JA_CADASTRADO" ? (<div><Badge className="bg-blue-100 text-blue-700 text-xs">Ja cadastrado{s.vinculo_por === "TELEFONE" ? " (tel)" : ""}</Badge>{s.cliente_nome ? <div className="text-[10px] text-gray-600 mt-0.5">{s.cliente_nome}</div> : null}{s.cliente_cpf ? <div className="text-[10px] text-gray-400">CPF: {s.cliente_cpf}</div> : null}{s.instituicao_vinculada ? <div className="text-[10px] text-gray-400">{s.instituicao_vinculada}</div> : null}</div>)
                            : s.cadastro_status === "VINCULO_TELEFONE" ? (<div><Badge className="bg-indigo-100 text-indigo-700 text-xs">Cliente (telefone)</Badge>{s.cliente_nome ? <div className="text-[10px] text-gray-600 mt-0.5">{s.cliente_nome}</div> : null}{s.cliente_cpf ? <div className="text-[10px] text-gray-400">CPF: {s.cliente_cpf}</div> : null}{s.instituicao_vinculada ? <div className="text-[10px] text-gray-400">{s.instituicao_vinculada}</div> : null}</div>)
                            : s.cadastro_status === "CPF_INVALIDO" ? (<Badge className="bg-red-100 text-red-700 text-xs">CPF invalido</Badge>)
                            : s.cadastro_status === "IGNORADO" ? (<Badge className="bg-gray-100 text-gray-500 text-xs">Sem segmento</Badge>)
                            : s.cadastro_status === "NAO_LOCALIZADO" ? (<Badge className="bg-gray-100 text-gray-500 text-xs">Nao localizado (NV)</Badge>)
                            : s.cadastro_status === "ERRO" ? (<Badge className="bg-red-100 text-red-700 text-xs">Erro</Badge>)
                            : (<span className="text-xs text-gray-300">-</span>)}
                          </TableCell>
                          <TableCell data-testid={`sol-atendimento-${s.id}`}>
                            {s.atendimento_status === "FINALIZADO" ? (<Badge className="bg-green-100 text-green-700 text-xs">Finalizado</Badge>)
                            : s.atendimento_status === "ADIADO" ? (<Badge className="bg-amber-100 text-amber-700 text-xs">Adiado</Badge>)
                            : (<Badge className="bg-blue-100 text-blue-700 text-xs">Ativo</Badge>)}
                          </TableCell>
                          <TableCell data-testid={`sol-ocorrencia-${s.id}`} className="max-w-[280px]">
                            {(s.ocorrencias && s.ocorrencias.length > 0) ? (
                              <div className="space-y-1.5 max-h-[160px] overflow-y-auto pr-1">
                                {[...s.ocorrencias].reverse().map((oc, i) => (
                                  <div key={oc.id || i} className="text-xs text-gray-700 border-l-2 border-purple-200 pl-2">
                                    <div className="whitespace-pre-wrap break-words">{oc.texto}</div>
                                    <div className="text-[10px] text-gray-400 mt-0.5">
                                      {oc.atendente ? `${oc.atendente} • ` : ""}
                                      {oc.data ? new Date(oc.data).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : ""}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (<span className="text-xs text-gray-300">-</span>)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Aba TRANSFERIDA (URA nao concluida) === */}
        <TabsContent value="transferidas" className="space-y-4" data-testid="tab-transferidas-incompletas">
          <Card data-testid="card-transferidas-incompletas">
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-orange-600" /> TRANSFERIDA (URA nao concluida)</CardTitle>
                  <CardDescription>Clientes que pressionaram tecla na URA reversa mas desligaram antes de informar CPF/segmento ({uraTransferidas.length})</CardDescription>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white shrink-0" onClick={() => abrirSmsEtapa("transferida_incompleta", "TRANSFERIDA (URA nao concluida)")} data-testid="btn-enviar-sms-transferidas">
                  <MessageSquare className="h-4 w-4 mr-1" /> Enviar SMS
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {uraTransferidas.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">Nenhuma chamada transferida sem conclusao</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Campanha</TableHead>
                        <TableHead>Tecla DTMF</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tag Cliente</TableHead>
                        <TableHead>Cliente (CRM)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uraTransferidas.map(t => (
                        <TableRow key={t.id}>
                          <TableCell className="text-xs">{t.data_hora ? new Date(t.data_hora).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "-"}</TableCell>
                          <TableCell className="font-mono text-xs">{t.telefone}</TableCell>
                          <TableCell className="text-xs">{t.campanha_nome || "-"}</TableCell>
                          <TableCell>{t.dtmf_pressionado ? <Badge className="bg-purple-100 text-purple-700 text-xs">{t.dtmf_pressionado}</Badge> : <span className="text-xs text-gray-300">-</span>}</TableCell>
                          <TableCell><Badge className="bg-orange-100 text-orange-700 text-xs">TRANSFERIDA</Badge></TableCell>
                          <TableCell>{(t.tags_cliente && t.tags_cliente.length > 0) ? (<div className="flex flex-wrap gap-1" data-testid={`transf-tags-${t.telefone}`}>{t.tags_cliente.map((tg, i) => (<Badge key={i} className="text-xs whitespace-nowrap border-0" style={{ color: tg.cor, backgroundColor: tg.bgCor }}>{tg.nome}</Badge>))}</div>) : (t.tag_cliente ? (<Badge className="bg-amber-100 text-amber-700 text-xs whitespace-nowrap">{t.tag_cliente}</Badge>) : (<span className="text-xs text-gray-300">-</span>))}</TableCell>
                          <TableCell>{t.cliente_nome ? (<div><Badge className="bg-indigo-100 text-indigo-700 text-xs">{t.cliente_classificacao === "INTERNO" ? "Interno" : "Cliente"}</Badge><div className="text-[10px] text-gray-600 mt-0.5">{t.cliente_nome}</div>{t.cliente_cpf ? <div className="text-[10px] text-gray-400">CPF: {t.cliente_cpf}</div> : null}{t.instituicao_vinculada ? <div className="text-[10px] text-gray-400">{t.instituicao_vinculada}</div> : null}</div>) : (<span className="text-xs text-gray-300">-</span>)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Aba OCUPADOS/REJEITADOS === */}
        <TabsContent value="ocupados" className="space-y-4" data-testid="tab-ocupados">
          <Card data-testid="card-ocupados">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-orange-600" /> Ocupados / Rejeitados</CardTitle>
              <CardDescription>Clientes cuja ligacao saiu mas o numero estava ocupado ou rejeitou ({ocupados.length})</CardDescription>
            </CardHeader>
            <CardContent>
              {ocupados.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">Nenhuma chamada ocupada/rejeitada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Campanha</TableHead>
                        <TableHead>Tentativas</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tag Cliente</TableHead>
                        <TableHead>Cliente (CRM)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ocupados.map(o => (
                        <TableRow key={o.id} data-testid={`ocupado-row-${o.id}`}>
                          <TableCell className="text-xs">{o.updated_at ? new Date(o.updated_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "-"}</TableCell>
                          <TableCell className="font-mono text-xs">{o.numero}</TableCell>
                          <TableCell className="text-xs">{o.campanha_nome || "-"}</TableCell>
                          <TableCell className="text-xs">{o.tentativas || 1}</TableCell>
                          <TableCell><Badge className="bg-orange-100 text-orange-700 text-xs">OCUPADO</Badge></TableCell>
                          <TableCell>{(o.tags_cliente && o.tags_cliente.length > 0) ? (<div className="flex flex-wrap gap-1">{o.tags_cliente.map((tg, i) => (<Badge key={i} className="text-xs whitespace-nowrap border-0" style={{ color: tg.cor, backgroundColor: tg.bgCor }}>{tg.nome}</Badge>))}</div>) : (<span className="text-xs text-gray-300">-</span>)}</TableCell>
                          <TableCell>{o.cliente_nome ? (<div><Badge className="bg-indigo-100 text-indigo-700 text-xs">{o.cliente_classificacao === "INTERNO" ? "Interno" : "Cliente"}</Badge><div className="text-[10px] text-gray-600 mt-0.5">{o.cliente_nome}</div>{o.cliente_cpf ? <div className="text-[10px] text-gray-400">CPF: {o.cliente_cpf}</div> : null}{o.instituicao_vinculada ? <div className="text-[10px] text-gray-400">{o.instituicao_vinculada}</div> : null}</div>) : (<span className="text-xs text-gray-300">-</span>)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Aba ATENDIDAS (NAO TRANSFERIDA) === */}
        <TabsContent value="atendidas_nt" className="space-y-4" data-testid="tab-atendidas-nt">
          <Card data-testid="card-atendidas-nt">
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-green-600" /> Atendidas (nao transferida)</CardTitle>
                  <CardDescription>Clientes que atenderam e escutaram o audio mas NAO apertaram 1 ({atendidasNt.length})</CardDescription>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white shrink-0" onClick={() => abrirSmsEtapa("atendida_nao_transf", "Atendidas (nao transf.)")} data-testid="btn-enviar-sms-atendidas-nt">
                  <MessageSquare className="h-4 w-4 mr-1" /> Enviar SMS
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {atendidasNt.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">Nenhuma chamada atendida sem transferencia</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Campanha</TableHead>
                        <TableHead>Tempo de audio</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Tag Cliente</TableHead>
                        <TableHead>Cliente (CRM)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {atendidasNt.map(a => (
                        <TableRow key={a.id} data-testid={`atendida-nt-row-${a.id}`}>
                          <TableCell className="text-xs">{(a.data_atendimento || a.updated_at) ? new Date(a.data_atendimento || a.updated_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "-"}</TableCell>
                          <TableCell className="font-mono text-xs">{a.numero}</TableCell>
                          <TableCell className="text-xs">{a.campanha_nome || "-"}</TableCell>
                          <TableCell className="text-xs">{a.duracao ? `${a.duracao}s` : <span className="text-gray-300">-</span>}</TableCell>
                          <TableCell><Badge className="bg-green-100 text-green-700 text-xs">ATENDIDA</Badge></TableCell>
                          <TableCell>{(a.tags_cliente && a.tags_cliente.length > 0) ? (<div className="flex flex-wrap gap-1">{a.tags_cliente.map((tg, i) => (<Badge key={i} className="text-xs whitespace-nowrap border-0" style={{ color: tg.cor, backgroundColor: tg.bgCor }}>{tg.nome}</Badge>))}</div>) : (<span className="text-xs text-gray-300">-</span>)}</TableCell>
                          <TableCell>{a.cliente_nome ? (<div><Badge className="bg-indigo-100 text-indigo-700 text-xs">{a.cliente_classificacao === "INTERNO" ? "Interno" : "Cliente"}</Badge><div className="text-[10px] text-gray-600 mt-0.5">{a.cliente_nome}</div>{a.cliente_cpf ? <div className="text-[10px] text-gray-400">CPF: {a.cliente_cpf}</div> : null}{a.instituicao_vinculada ? <div className="text-[10px] text-gray-400">{a.instituicao_vinculada}</div> : null}</div>) : (<span className="text-xs text-gray-300">-</span>)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="atendidas_tr" className="space-y-4" data-testid="tab-atendidas-tr">
          <Card data-testid="card-atendidas-tr">
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> Atendidas (transferida)</CardTitle>
                  <CardDescription>Clientes que ouviram o audio completo e foram transferidos AUTOMATICAMENTE, sem teclar 1 ({atendidasTr.length})</CardDescription>
                </div>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white shrink-0" onClick={() => abrirSmsEtapa("atendida_transferida", "Atendidas (transferida)")} data-testid="btn-enviar-sms-atendidas-tr">
                  <MessageSquare className="h-4 w-4 mr-1" /> Enviar SMS
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {atendidasTr.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">Nenhuma chamada atendida com transferencia</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Campanha</TableHead>
                        <TableHead>Tempo de audio</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Concluiu URA?</TableHead>
                        <TableHead>Tag Cliente</TableHead>
                        <TableHead>Cliente (CRM)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {atendidasTr.map(a => (
                        <TableRow key={a.id} data-testid={`atendida-tr-row-${a.id}`}>
                          <TableCell className="text-xs">{(a.data_atendimento || a.updated_at) ? new Date(a.data_atendimento || a.updated_at).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "-"}</TableCell>
                          <TableCell className="font-mono text-xs">{a.numero}</TableCell>
                          <TableCell className="text-xs">{a.campanha_nome || "-"}</TableCell>
                          <TableCell className="text-xs">{a.duracao ? `${a.duracao}s` : <span className="text-gray-300">-</span>}</TableCell>
                          <TableCell><Badge className="bg-blue-100 text-blue-700 text-xs">TRANSFERIDA</Badge></TableCell>
                          <TableCell>{a.concluiu_ura ? <Badge className="bg-green-100 text-green-700 text-xs">Sim</Badge> : <Badge className="bg-gray-100 text-gray-500 text-xs">Nao</Badge>}</TableCell>
                          <TableCell>{(a.tags_cliente && a.tags_cliente.length > 0) ? (<div className="flex flex-wrap gap-1">{a.tags_cliente.map((tg, i) => (<Badge key={i} className="text-xs whitespace-nowrap border-0" style={{ color: tg.cor, backgroundColor: tg.bgCor }}>{tg.nome}</Badge>))}</div>) : (<span className="text-xs text-gray-300">-</span>)}</TableCell>
                          <TableCell>{a.cliente_nome ? (<div><Badge className="bg-indigo-100 text-indigo-700 text-xs">{a.cliente_classificacao === "INTERNO" ? "Interno" : "Cliente"}</Badge><div className="text-[10px] text-gray-600 mt-0.5">{a.cliente_nome}</div>{a.cliente_cpf ? <div className="text-[10px] text-gray-400">CPF: {a.cliente_cpf}</div> : null}{a.instituicao_vinculada ? <div className="text-[10px] text-gray-400">{a.instituicao_vinculada}</div> : null}</div>) : (<span className="text-xs text-gray-300">-</span>)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ======= MODALS ======= */}

      {/* Enviar SMS por etapa de ligacao */}
      <Dialog open={smsEtapa.open} onOpenChange={(o) => !smsEtapaSending && setSmsEtapa(s => ({ ...s, open: o }))}>
        <DialogContent className="max-w-lg" data-testid="dialog-sms-etapa">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-green-600" /> Enviar SMS</DialogTitle>
            <DialogDescription>Etapa: <strong>{smsEtapa.label}</strong>. Escolha o modelo e o periodo. Os numeros serao enviados via uma campanha SMS (chips, rate-limit e DLR).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Modelo (template) do SMS</Label>
              <Select value={smsEtapaTemplate} onValueChange={setSmsEtapaTemplate}>
                <SelectTrigger data-testid="select-sms-etapa-template"><SelectValue placeholder="Selecione um modelo" /></SelectTrigger>
                <SelectContent>
                  {smsTemplates.length === 0 ? (
                    <SelectItem value="__none__" disabled>Nenhum modelo cadastrado</SelectItem>
                  ) : smsTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id} data-testid={`sms-etapa-tpl-${t.id}`}>{t.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {smsEtapaTemplate && (
                <p className="text-xs text-muted-foreground bg-gray-50 border rounded p-2 mt-1 whitespace-pre-wrap" data-testid="sms-etapa-preview-texto">
                  {smsTemplates.find(t => t.id === smsEtapaTemplate)?.texto || ""}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Periodo</Label>
              <Select value={smsEtapaPeriodo} onValueChange={setSmsEtapaPeriodo}>
                <SelectTrigger data-testid="select-sms-etapa-periodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoje">Hoje</SelectItem>
                  <SelectItem value="7d">Ultimos 7 dias</SelectItem>
                  <SelectItem value="30d">Ultimos 30 dias</SelectItem>
                  <SelectItem value="todos">Todo o historico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Campanha (vinculada ao numero)</Label>
              <Select value={smsEtapaCampanha} onValueChange={setSmsEtapaCampanha}>
                <SelectTrigger data-testid="select-sms-etapa-campanha"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as campanhas</SelectItem>
                  {campanhas.map(cp => (
                    <SelectItem key={cp.id} value={cp.id} data-testid={`sms-etapa-camp-${cp.id}`}>{cp.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="check-sms-etapa-naoenviados">
              <input type="checkbox" className="h-4 w-4 accent-green-600" checked={smsEtapaNaoEnviados} onChange={(e) => setSmsEtapaNaoEnviados(e.target.checked)} />
              <span className="text-gray-700">Enviar apenas para quem ainda <strong>nao recebeu SMS</strong> (com sucesso)</span>
            </label>
            <div className="rounded-md border bg-green-50 p-3 text-sm flex items-center justify-between" data-testid="sms-etapa-count-box">
              <span className="text-gray-600">Numeros distintos a receber o SMS:</span>
              <span className="font-bold text-green-700 text-lg">
                {smsEtapaCounting ? "..." : (smsEtapaCount === null ? "-" : smsEtapaCount.toLocaleString("pt-BR"))}
              </span>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 space-y-3" data-testid="sms-auto-section">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none" data-testid="check-sms-auto-ativo">
                <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={autoSmsAtivo} onChange={(e) => setAutoSmsAtivo(e.target.checked)} />
                <span className="text-blue-800">Enviar SMS automaticamente nesta etapa</span>
              </label>
              <p className="text-xs text-blue-700">O sistema dispara o SMS sozinho para cada numero novo desta etapa (via campanha normal, registrada no relatorio). Nunca envia duplicado.</p>
              {autoSmsAtivo && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Modelo (template) do automatico</Label>
                    <Select value={autoSmsTemplate} onValueChange={setAutoSmsTemplate}>
                      <SelectTrigger className="h-9" data-testid="select-sms-auto-template"><SelectValue placeholder="Selecione um modelo" /></SelectTrigger>
                      <SelectContent>
                        {smsTemplates.map(t => (<SelectItem key={t.id} value={t.id} data-testid={`sms-auto-tpl-${t.id}`}>{t.nome}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    {autoSmsTemplate && (
                      <p className="text-xs text-muted-foreground bg-white border rounded p-2 mt-1 whitespace-pre-wrap" data-testid="sms-auto-preview-texto">
                        {smsTemplates.find(t => t.id === autoSmsTemplate)?.texto || ""}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Periodo</Label>
                    <Select value={autoSmsPeriodo} onValueChange={setAutoSmsPeriodo}>
                      <SelectTrigger className="h-9" data-testid="select-sms-auto-periodo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hoje">Hoje</SelectItem>
                        <SelectItem value="7d">Ultimos 7 dias</SelectItem>
                        <SelectItem value="30d">Ultimos 30 dias</SelectItem>
                        <SelectItem value="todos">Todo o historico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <Button size="sm" variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-100" onClick={salvarAutoSms} disabled={autoSmsSaving} data-testid="btn-salvar-sms-auto">
                {autoSmsSaving ? "Salvando..." : "Salvar automatico"}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsEtapa(s => ({ ...s, open: false }))} disabled={smsEtapaSending} data-testid="btn-sms-etapa-cancelar">Cancelar</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={enviarSmsEtapa} disabled={smsEtapaSending || smsEtapaCounting || !smsEtapaTemplate || !smsEtapaCount} data-testid="btn-sms-etapa-confirmar">
              <Send className="h-4 w-4 mr-1" />
              {smsEtapaSending ? "Enviando..." : `Enviar agora${smsEtapaCount ? ` (${smsEtapaCount.toLocaleString("pt-BR")})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="modal-config-voip">
          <DialogHeader>
            <DialogTitle>Configurar Central VoIP</DialogTitle>
            <DialogDescription>Configure o gateway DINSTAR e o Asterisk AMI</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-medium">Gateway DINSTAR</p>
              <p className="text-xs text-blue-600">Ja configurado via .env (DINSTAR_URL). Status: {config.configurado ? "Conectado" : "Desconectado"}</p>
              {config.gateways && (
                <div className="mt-1 space-y-0.5">
                  {config.gateways.map(g => (
                    <p key={g.id} className="text-xs text-blue-500">{g.nome}: {g.url}</p>
                  ))}
                </div>
              )}
            </div>
            <Separator />
            <p className="text-xs text-gray-500 font-medium">Asterisk AMI (para chamadas)</p>
            <div>
              <Label>Host AMI (IP do Asterisk) *</Label>
              <Input value={configForm.ami_host} onChange={e => setConfigForm({ ...configForm, ami_host: e.target.value })} placeholder="192.168.0.100" data-testid="input-ami-host" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Porta AMI</Label><Input value={configForm.ami_port} onChange={e => setConfigForm({ ...configForm, ami_port: e.target.value })} placeholder="5038" /></div>
              <div><Label>Usuario AMI</Label><Input value={configForm.ami_user} onChange={e => setConfigForm({ ...configForm, ami_user: e.target.value })} placeholder="admin" /></div>
              <div><Label>Senha AMI</Label><Input type="password" value={configForm.ami_secret} onChange={e => setConfigForm({ ...configForm, ami_secret: e.target.value })} /></div>
            </div>
            <Separator />
            <p className="text-xs text-gray-500 font-medium">Configuracao de Chamadas</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Ramal de Saida (trunk)</Label><Input value={configForm.ramal_saida} onChange={e => setConfigForm({ ...configForm, ramal_saida: e.target.value })} placeholder="DINSTAR" /></div>
              <div><Label>Numero para Transferencia</Label><Input value={configForm.numero_transferencia} onChange={e => setConfigForm({ ...configForm, numero_transferencia: e.target.value })} placeholder="1140001234" /></div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={testarAMI} className="gap-1" data-testid="btn-testar-ami"><Zap className="h-4 w-4" /> Testar AMI</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
              <Button onClick={salvarConfig} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-config-voip">Salvar</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Audio */}
      <Dialog open={showUploadAudio} onOpenChange={setShowUploadAudio}>
        <DialogContent className="max-w-md" data-testid="modal-upload-audio">
          <DialogHeader><DialogTitle>Enviar Audio</DialogTitle><DialogDescription>Envie mensagem gravada para URA (.mp3, .wav, .ogg)</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome</Label><Input value={audioForm.nome} onChange={e => setAudioForm({ ...audioForm, nome: e.target.value })} placeholder="Ex: FGTS Saque Aniversario" data-testid="input-audio-nome" /></div>
            <div><Label>Descricao</Label><Input value={audioForm.descricao} onChange={e => setAudioForm({ ...audioForm, descricao: e.target.value })} placeholder="Descricao do audio" /></div>
            <div><Label>Arquivo de Audio *</Label><Input type="file" accept=".mp3,.wav,.ogg,.m4a" onChange={e => setAudioFile(e.target.files[0])} data-testid="input-audio-file" /></div>
            {audioFile && <p className="text-xs text-gray-500">{audioFile.name} ({(audioFile.size / 1024).toFixed(0)} KB)</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadAudio(false)}>Cancelar</Button>
            <Button onClick={uploadAudio} disabled={uploading} className="bg-green-600 hover:bg-green-700 gap-1" data-testid="btn-enviar-audio">
              {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adicionar Mais Contatos a Campanha Existente */}
      <Dialog open={!!adicionarContatosModal} onOpenChange={(o) => { if (!o) setAdicionarContatosModal(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar Mais Numeros</DialogTitle>
            <DialogDescription>
              Inclua novos contatos em <b>{adicionarContatosModal?.nome}</b>. Numeros ja chamados e blacklist sao ignorados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Grupo de Contatos</Label>
              <Select value={adicionarContatosModal?.grupo_contato || " "} onValueChange={v => setAdicionarContatosModal({...adicionarContatosModal, grupo_contato: v === " " ? "" : v})}>
                <SelectTrigger className="h-9" data-testid="select-grupo-adicionar"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Todos</SelectItem>
                  {grupos.map(g => <SelectItem key={g.nome} value={g.nome}>{g.nome} ({g.qtd})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
              <Label className="text-xs font-semibold text-slate-700">Filtros</Label>
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-[11px] text-gray-500">Tipo:</span>
                {["todos","celular","fixo","invalido","nao_validado"].map(v => (
                  <Badge key={v} variant={adicionarForm.tipo_telefone === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setAdicionarForm({...adicionarForm, tipo_telefone: v})}>
                    {v === "todos" ? "Todos" : v === "celular" ? "Celular" : v === "fixo" ? "Fixo" : v === "invalido" ? "Invalido" : "Nao validado"}
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-[11px] text-gray-500">WhatsApp:</span>
                {[["todos","Todos"],["sim","Com WhatsApp"],["nao","Sem WhatsApp"]].map(([v,l]) => (
                  <Badge key={v} variant={adicionarForm.whatsapp_status === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setAdicionarForm({...adicionarForm, whatsapp_status: v})}>{l}</Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-[11px] text-gray-500">SMS:</span>
                {[["todos","Todos"],["enviado","Ja enviado"],["nao_enviado","Nao enviado"]].map(([v,l]) => (
                  <Badge key={v} variant={adicionarForm.sms_status === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setAdicionarForm({...adicionarForm, sms_status: v})}>{l}</Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-[11px] text-gray-500">Acao:</span>
                {[["todos","Todos"],["sem_acao","Sem acao"],["so_sms","So SMS"],["so_ligacao","So Ligacao"],["ambos","SMS+Ligacao"]].map(([v,l]) => (
                  <Badge key={v} variant={adicionarForm.acao === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setAdicionarForm({...adicionarForm, acao: v})}>{l}</Badge>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                <Label className="text-[11px] text-gray-600 whitespace-nowrap">Quantidade Maxima:</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="0 = todos"
                  value={adicionarForm.limite_contatos || ""}
                  onChange={e => setAdicionarForm({...adicionarForm, limite_contatos: parseInt(e.target.value) || 0})}
                  className="h-7 text-xs w-32"
                  data-testid="input-limite-adicionar"
                />
                <span className="text-[11px] text-gray-500">contatos</span>
              </div>
            </div>
            <p className="text-xs text-gray-500">Se a campanha estiver FINALIZADA, ela voltara para PAUSADA apos a inclusao - use <b>Retomar</b> para continuar.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={adicionandoContatos} onClick={() => setAdicionarContatosModal(null)}>Cancelar</Button>
            <Button onClick={adicionarMaisContatos} disabled={adicionandoContatos} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50" data-testid="btn-confirmar-adicionar-contatos">
              {adicionandoContatos ? "Adicionando..." : "Adicionar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nova Campanha URA */}
      <Dialog open={showNovaCampanha} onOpenChange={setShowNovaCampanha}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="modal-nova-campanha-ura">
          <DialogHeader><DialogTitle>Nova Campanha URA Reversa</DialogTitle><DialogDescription>Configure a campanha de discagem automatica</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div><Label>Nome da Campanha *</Label><Input value={campanhaForm.nome} onChange={e => setCampanhaForm({ ...campanhaForm, nome: e.target.value })} placeholder="Ex: FGTS Marco 2026" data-testid="input-campanha-ura-nome" /></div>
            <div>
              <Label>Audio da URA</Label>
              <Select value={campanhaForm.audio_id} onValueChange={v => setCampanhaForm({ ...campanhaForm, audio_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecionar audio" /></SelectTrigger>
                <SelectContent>{audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grupo de Contatos</Label>
              <Select value={campanhaForm.grupo_contato} onValueChange={v => setCampanhaForm({ ...campanhaForm, grupo_contato: v })}>
                <SelectTrigger><SelectValue placeholder="Todos os contatos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Todos</SelectItem>
                  {grupos.map(g => <SelectItem key={g.nome} value={g.nome}>{g.nome} ({g.qtd})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {campanhaForm.grupo_contato && (
              <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-700">Filtros Avançados</Label>
                  <Badge className="bg-purple-600 text-white text-xs">
                    {campanhaPreview.total_validos?.toLocaleString("pt-BR") || 0} de {campanhaPreview.total_no_grupo?.toLocaleString("pt-BR") || 0} contatos
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[11px] text-gray-500">Tipo:</span>
                  {["todos","celular","fixo","invalido","nao_validado"].map(v => (
                    <Badge key={v} variant={campanhaForm.tipo_telefone === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, tipo_telefone: v})}>
                      {v === "todos" ? "Todos" : v === "celular" ? "Celular" : v === "fixo" ? "Fixo" : v === "invalido" ? "Invalido" : "Nao validado"}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[11px] text-gray-500">WhatsApp:</span>
                  {[["todos","Todos"],["sim","Com WhatsApp"],["nao","Sem WhatsApp"]].map(([v,l]) => (
                    <Badge key={v} variant={campanhaForm.whatsapp_status === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, whatsapp_status: v})}>{l}</Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[11px] text-gray-500">Status SMS:</span>
                  {[["todos","Todos"],["enviado","Ja enviado"],["nao_enviado","Nao enviado"]].map(([v,l]) => (
                    <Badge key={v} variant={campanhaForm.sms_status === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, sms_status: v})}>{l}</Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[11px] text-gray-500">Acao:</span>
                  {[["todos","Todos"],["sem_acao","Sem acao"],["so_sms","So SMS"],["so_ligacao","So Ligacao"],["ambos","SMS+Ligacao"]].map(([v,l]) => (
                    <Badge key={v} variant={campanhaForm.acao === v ? "default" : "outline"} className="cursor-pointer text-xs" onClick={() => setCampanhaForm({...campanhaForm, acao: v})}>{l}</Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                  <Label className="text-[11px] text-gray-600 whitespace-nowrap">Quantidade Maxima:</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder="0 = todos"
                    value={campanhaForm.limite_contatos || ""}
                    onChange={e => setCampanhaForm({...campanhaForm, limite_contatos: parseInt(e.target.value) || 0})}
                    className="h-7 text-xs w-32"
                    data-testid="input-limite-contatos"
                  />
                  <span className="text-[11px] text-gray-500">contatos (0 = todos)</span>
                  {campanhaForm.limite_contatos > 0 && (
                    <Badge className="bg-orange-100 text-orange-700 text-[10px]">
                      Sera limitado a {campanhaForm.limite_contatos.toLocaleString("pt-BR")}
                    </Badge>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Horario Inicio</Label><Input type="time" value={campanhaForm.horario_inicio} onChange={e => setCampanhaForm({ ...campanhaForm, horario_inicio: e.target.value })} /></div>
              <div><Label>Horario Fim</Label><Input type="time" value={campanhaForm.horario_fim} onChange={e => setCampanhaForm({ ...campanhaForm, horario_fim: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Max Tentativas</Label><Input type="number" value={campanhaForm.tentativas_max} onChange={e => setCampanhaForm({ ...campanhaForm, tentativas_max: parseInt(e.target.value) || 3 })} /></div>
              <div><Label>Intervalo (min)</Label><Input type="number" value={campanhaForm.intervalo_tentativas} onChange={e => setCampanhaForm({ ...campanhaForm, intervalo_tentativas: parseInt(e.target.value) || 30 })} /></div>
              <div><Label>Simultaneas (max 48)</Label><Input type="number" min={1} max={48} value={campanhaForm.chamadas_simultaneas} onChange={e => setCampanhaForm({ ...campanhaForm, chamadas_simultaneas: Math.min(48, Math.max(1, parseInt(e.target.value) || 5)) })} /><p className="text-xs text-gray-400 mt-1">Distribuidas entre gateways</p></div>
            </div>
            <Separator />
            <div>
              <Label className="font-semibold">Fluxo DTMF (Teclas)</Label>
              <p className="text-xs text-gray-500 mb-2">Configure a acao para cada tecla pressionada pelo cliente</p>
              {Object.entries(campanhaForm.fluxo_dtmf).map(([tecla, cfg]) => (
                <div key={tecla} className="flex flex-col gap-2 mb-3 p-3 rounded-lg border bg-gray-50">
                  <div className="flex gap-2 items-center">
                    <Badge className="bg-purple-100 text-purple-700 shrink-0">Tecla {tecla}</Badge>
                    <Input className="flex-1 h-8" value={cfg.descricao} onChange={e => setCampanhaForm(prev => ({ ...prev, fluxo_dtmf: { ...prev.fluxo_dtmf, [tecla]: { ...cfg, descricao: e.target.value } } }))} placeholder="Descricao ex: Tenho interesse" />
                    <Select value={cfg.acao} onValueChange={v => setCampanhaForm(prev => ({ ...prev, fluxo_dtmf: { ...prev.fluxo_dtmf, [tecla]: { ...cfg, acao: v, destino: "", sms_template_id: "", wpp_flow_id: "" } } }))}>
                      <SelectTrigger className="w-[160px] h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enviar_sms_whatsapp">SMS + WhatsApp</SelectItem>
                        <SelectItem value="enviar_whatsapp">Enviar WhatsApp</SelectItem>
                        <SelectItem value="enviar_sms">Enviar SMS</SelectItem>
                        <SelectItem value="screen_pop">Screen Pop (SMS + WhatsApp)</SelectItem>
                        <SelectItem value="screen_pop_sms">Screen Pop (Só SMS)</SelectItem>
                        <SelectItem value="screen_pop_wpp">Screen Pop (Só WhatsApp)</SelectItem>
                        <SelectItem value="transferir_ramal">Transferir Ramal</SelectItem>
                        <SelectItem value="blacklist">Blacklist</SelectItem>
                        <SelectItem value="transferir_receptivo">Transferir Receptivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(cfg.acao?.includes("sms") || cfg.acao === "enviar_sms_whatsapp") && cfg.acao !== "screen_pop" && (
                    <div className="flex gap-2 items-center ml-6">
                      <span className="text-xs text-gray-500 w-16 shrink-0">SMS:</span>
                      <Select value={cfg.sms_template_id || ""} onValueChange={v => {
                        const tpl = smsTemplates.find(t => t.id === v);
                        setCampanhaForm(prev => ({ ...prev, fluxo_dtmf: { ...prev.fluxo_dtmf, [tecla]: { ...cfg, sms_template_id: v, destino: tpl?.texto || cfg.destino } } }));
                      }}>
                        <SelectTrigger className="flex-1 h-8"><SelectValue placeholder="Selecione Template SMS" /></SelectTrigger>
                        <SelectContent>
                          {smsTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {(cfg.acao?.includes("whatsapp") || cfg.acao === "enviar_sms_whatsapp") && (
                    <div className="flex gap-2 items-center ml-6">
                      <span className="text-xs text-gray-500 w-16 shrink-0">WhatsApp:</span>
                      <Select value={cfg.wpp_flow_id || ""} onValueChange={v => setCampanhaForm(prev => ({ ...prev, fluxo_dtmf: { ...prev.fluxo_dtmf, [tecla]: { ...cfg, wpp_flow_id: v } } }))}>
                        <SelectTrigger className="flex-1 h-8"><SelectValue placeholder="Selecione Flow WhatsApp" /></SelectTrigger>
                        <SelectContent>
                          {wppFlows.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {cfg.acao === "transferir_ramal" && (
                    <div className="flex gap-2 items-center ml-6">
                      <span className="text-xs text-gray-500 w-16 shrink-0">Ramal:</span>
                      <Input className="flex-1 h-8" value={cfg.destino || ""} onChange={e => setCampanhaForm(prev => ({ ...prev, fluxo_dtmf: { ...prev.fluxo_dtmf, [tecla]: { ...cfg, destino: e.target.value } } }))} placeholder="Numero do ramal" />
                    </div>
                  )}
                  {cfg.acao?.startsWith("screen_pop") && (
                    <div className="ml-6 p-2 rounded bg-cyan-950/30 border border-cyan-800/20">
                      <span className="text-xs text-cyan-400">
                        {cfg.acao === "screen_pop" && "Envia SMS + WhatsApp com link da Landing Page (configure abaixo na seção Screen Pop)"}
                        {cfg.acao === "screen_pop_sms" && "Envia somente SMS com link da Landing Page (configure abaixo na seção Screen Pop)"}
                        {cfg.acao === "screen_pop_wpp" && "Envia somente WhatsApp com link da Landing Page (configure abaixo na seção Screen Pop)"}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Screen Pop Config */}
          <div className="border rounded-lg p-4 bg-gradient-to-r from-cyan-950/30 to-purple-950/30 border-cyan-800/30">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-cyan-300">Screen Pop (SMS+WhatsApp com Link)</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={campanhaForm.screen_pop?.ativo || false}
                  onChange={e => setCampanhaForm(prev => ({ ...prev, screen_pop: { ...prev.screen_pop, ativo: e.target.checked } }))}
                  className="w-4 h-4 rounded" data-testid="chk-screen-pop-ativo" />
                <span className="text-xs text-gray-400">Ativar</span>
              </label>
            </div>
            {campanhaForm.screen_pop?.ativo && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-400">Titulo da Pagina</label>
                    <Input className="h-8" value={campanhaForm.screen_pop?.titulo || ""} placeholder="Ex: Oferta Especial"
                      onChange={e => setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, titulo: e.target.value } }))}
                      data-testid="input-sp-titulo" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Nome da Empresa</label>
                    <Input className="h-8" value={campanhaForm.screen_pop?.empresa || "StiloCred"}
                      onChange={e => setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, empresa: e.target.value } }))}
                      data-testid="input-sp-empresa" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-400">Mensagem da Pagina</label>
                  <Input className="h-8" value={campanhaForm.screen_pop?.mensagem || ""}
                    onChange={e => setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, mensagem: e.target.value } }))}
                    placeholder="Temos uma oferta especial para voce!" data-testid="input-sp-mensagem" />
                </div>
                <div>
                  <label className="text-xs text-gray-400">Texto do SMS (use {"{link}"} para o link)</label>
                  <Input className="h-8" value={campanhaForm.screen_pop?.sms_msg || ""}
                    onChange={e => setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, sms_msg: e.target.value } }))}
                    data-testid="input-sp-sms" />
                </div>
                <div>
                  <label className="text-xs text-gray-400">Texto do WhatsApp (use {"{link}"} para o link)</label>
                  <Input className="h-8" value={campanhaForm.screen_pop?.wpp_msg || ""}
                    onChange={e => setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, wpp_msg: e.target.value } }))}
                    data-testid="input-sp-wpp" />
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={campanhaForm.screen_pop?.enviar_sms !== false}
                      onChange={e => setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, enviar_sms: e.target.checked } }))}
                      className="w-3.5 h-3.5 rounded" />
                    <span className="text-xs text-gray-400">Enviar SMS</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={campanhaForm.screen_pop?.enviar_wpp !== false}
                      onChange={e => setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, enviar_wpp: e.target.checked } }))}
                      className="w-3.5 h-3.5 rounded" />
                    <span className="text-xs text-gray-400">Enviar WhatsApp</span>
                  </label>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Botoes da Landing Page</label>
                  {(campanhaForm.screen_pop?.botoes || []).map((btn, i) => (
                    <div key={i} className="flex gap-2 items-center mb-2">
                      <Input className="flex-1 h-7 text-xs" value={btn.label} placeholder="Texto do botao"
                        onChange={e => {
                          const nb = [...(campanhaForm.screen_pop?.botoes || [])];
                          nb[i] = { ...nb[i], label: e.target.value };
                          setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, botoes: nb } }));
                        }} />
                      <select className="h-7 text-xs rounded border bg-gray-900 border-gray-700 px-1"
                        value={btn.estilo || "primary"}
                        onChange={e => {
                          const nb = [...(campanhaForm.screen_pop?.botoes || [])];
                          nb[i] = { ...nb[i], estilo: e.target.value };
                          setCampanhaForm(p => ({ ...p, screen_pop: { ...p.screen_pop, botoes: nb } }));
                        }}>
                        <option value="primary">Destaque</option>
                        <option value="secondary">Normal</option>
                      </select>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" className="text-xs text-cyan-400 h-6 px-2"
                    onClick={() => setCampanhaForm(p => ({
                      ...p, screen_pop: { ...p.screen_pop, botoes: [...(p.screen_pop?.botoes || []), { label: "", acao: "custom", estilo: "secondary" }] }
                    }))}>+ Adicionar Botao</Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovaCampanha(false)}>Cancelar</Button>
            <Button onClick={salvarCampanha} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-salvar-campanha-ura">Criar Campanha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Fluxo DTMF */}
      <Dialog open={!!showFluxo} onOpenChange={() => setShowFluxo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Fluxo DTMF - {showFluxo?.nome}</DialogTitle><DialogDescription>Configuracao de teclas da campanha</DialogDescription></DialogHeader>
          <div className="space-y-2 py-2">
            {showFluxo && Object.entries(showFluxo.fluxo_dtmf || {}).map(([tecla, cfg]) => (
              <div key={tecla} className="flex items-center gap-3 p-3 rounded-lg border bg-gray-50">
                <Badge className="bg-purple-600 text-white text-lg px-3">{tecla}</Badge>
                <div className="flex-1">
                  <p className="font-medium text-sm">{cfg.descricao || "Sem descricao"}</p>
                  <p className="text-xs text-gray-500">{cfg.acao}{cfg.destino ? ` -> ${cfg.destino}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Contatos */}
      <Dialog open={!!showImportContatos} onOpenChange={() => setShowImportContatos(null)}>
        <DialogContent className="max-w-md" data-testid="modal-import-contatos-voip">
          <DialogHeader><DialogTitle>Importar Contatos</DialogTitle><DialogDescription>Importe numeros de telefone para a campanha "{showImportContatos?.nome}"</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Arquivo (CSV, TXT)</Label>
              <Input type="file" accept=".csv,.txt,.xlsx" onChange={e => setImportFile(e.target.files[0])} />
            </div>
            <p className="text-xs text-gray-500">O sistema extrai automaticamente numeros de 10-13 digitos do arquivo.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportContatos(null)}>Cancelar</Button>
            <Button onClick={() => importarContatos(showImportContatos?.id)} className="bg-purple-600 hover:bg-purple-700 gap-1">
              <Upload className="h-4 w-4" /> Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Report */}
      <CampaignReportModal campanha={showReportCamp} open={!!showReportCamp} onClose={() => setShowReportCamp(null)} />

      {/* Modal Editar Campanha */}
      <Dialog open={showEditarCampanha} onOpenChange={setShowEditarCampanha}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="modal-editar-campanha">
          <DialogHeader>
            <DialogTitle>Editar Campanha</DialogTitle>
            <DialogDescription>Altere as configuracoes da campanha</DialogDescription>
          </DialogHeader>
          {editarCampanhaData && (
            <div className="space-y-4 py-2">
              <div className="grid gap-3">
                <div>
                  <Label>Nome</Label>
                  <Input value={editarCampanhaData.nome} onChange={e => setEditarCampanhaData(p => ({ ...p, nome: e.target.value }))} />
                </div>
                <div>
                  <Label>Audio</Label>
                  <Select value={editarCampanhaData.audio_id} onValueChange={v => setEditarCampanhaData(p => ({ ...p, audio_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecione audio" /></SelectTrigger>
                    <SelectContent>
                      {audios.map(a => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Horario Inicio</Label>
                    <Input type="time" value={editarCampanhaData.horario_inicio} onChange={e => setEditarCampanhaData(p => ({ ...p, horario_inicio: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Horario Fim</Label>
                    <Input type="time" value={editarCampanhaData.horario_fim} onChange={e => setEditarCampanhaData(p => ({ ...p, horario_fim: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Tentativas</Label>
                    <Input type="number" min={1} max={10} value={editarCampanhaData.tentativas_max} onChange={e => setEditarCampanhaData(p => ({ ...p, tentativas_max: parseInt(e.target.value) || 3 }))} />
                  </div>
                  <div>
                    <Label>Intervalo (min)</Label>
                    <Input type="number" min={1} value={editarCampanhaData.intervalo_tentativas} onChange={e => setEditarCampanhaData(p => ({ ...p, intervalo_tentativas: parseInt(e.target.value) || 30 }))} />
                  </div>
                  <div>
                    <Label>Simultaneas</Label>
                    <Input type="number" min={1} max={48} value={editarCampanhaData.chamadas_simultaneas} onChange={e => setEditarCampanhaData(p => ({ ...p, chamadas_simultaneas: Math.min(48, Math.max(1, parseInt(e.target.value) || 5)) }))} />
                  </div>
                </div>
              </div>
              <Separator />
              <div>
                <Label className="font-semibold">Fluxo DTMF (Teclas)</Label>
                <p className="text-xs text-gray-500 mb-2">Configure a acao para cada tecla pressionada pelo cliente</p>
                {Object.entries(editarCampanhaData.fluxo_dtmf || defaultDtmf).map(([tecla, cfg]) => (
                  <div key={tecla} className="flex flex-col gap-2 mb-3 p-3 rounded-lg border bg-gray-50">
                    <div className="flex gap-2 items-center">
                      <Badge className="bg-purple-100 text-purple-700 shrink-0">Tecla {tecla}</Badge>
                      <Input className="flex-1 h-8" value={cfg.descricao} onChange={e => setEditarCampanhaData(p => ({ ...p, fluxo_dtmf: { ...(p.fluxo_dtmf || defaultDtmf), [tecla]: { ...cfg, descricao: e.target.value } } }))} placeholder="Descricao" />
                      <Select value={cfg.acao} onValueChange={v => setEditarCampanhaData(p => ({ ...p, fluxo_dtmf: { ...(p.fluxo_dtmf || defaultDtmf), [tecla]: { ...cfg, acao: v, destino: "", sms_template_id: "", wpp_flow_id: "" } } }))}>
                        <SelectTrigger className="w-[160px] h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="enviar_sms_whatsapp">SMS + WhatsApp</SelectItem>
                          <SelectItem value="enviar_whatsapp">Enviar WhatsApp</SelectItem>
                          <SelectItem value="enviar_sms">Enviar SMS</SelectItem>
                          <SelectItem value="screen_pop">Screen Pop</SelectItem>
                          <SelectItem value="transferir_ramal">Transferir Ramal</SelectItem>
                          <SelectItem value="blacklist">Blacklist</SelectItem>
                          <SelectItem value="transferir_receptivo">Transferir Receptivo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(cfg.acao?.includes("sms") || cfg.acao === "enviar_sms_whatsapp") && cfg.acao !== "screen_pop" && (
                      <div className="flex gap-2 items-center ml-6">
                        <span className="text-xs text-gray-500 w-16 shrink-0">SMS:</span>
                        <Select value={cfg.sms_template_id || ""} onValueChange={v => {
                          const tpl = smsTemplates.find(t => t.id === v);
                          setEditarCampanhaData(p => ({ ...p, fluxo_dtmf: { ...(p.fluxo_dtmf || defaultDtmf), [tecla]: { ...cfg, sms_template_id: v, destino: tpl?.texto || cfg.destino } } }));
                        }}>
                          <SelectTrigger className="flex-1 h-8"><SelectValue placeholder="Selecione Template SMS" /></SelectTrigger>
                          <SelectContent>
                            {smsTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {(cfg.acao?.includes("whatsapp") || cfg.acao === "enviar_sms_whatsapp") && (
                      <div className="flex gap-2 items-center ml-6">
                        <span className="text-xs text-gray-500 w-16 shrink-0">WhatsApp:</span>
                        <Select value={cfg.wpp_flow_id || ""} onValueChange={v => setEditarCampanhaData(p => ({ ...p, fluxo_dtmf: { ...(p.fluxo_dtmf || defaultDtmf), [tecla]: { ...cfg, wpp_flow_id: v } } }))}>
                          <SelectTrigger className="flex-1 h-8"><SelectValue placeholder="Selecione Flow WhatsApp" /></SelectTrigger>
                          <SelectContent>
                            {wppFlows.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {cfg.acao === "transferir_ramal" && (
                      <div className="flex gap-2 items-center ml-6">
                        <span className="text-xs text-gray-500 w-16 shrink-0">Ramal:</span>
                        <Input className="flex-1 h-8" value={cfg.destino || ""} onChange={e => setEditarCampanhaData(p => ({ ...p, fluxo_dtmf: { ...(p.fluxo_dtmf || defaultDtmf), [tecla]: { ...cfg, destino: e.target.value } } }))} placeholder="Numero do ramal" />
                      </div>
                    )}
                    {cfg.acao === "screen_pop" && (
                      <div className="ml-6 p-2 rounded bg-cyan-950/30 border border-cyan-800/20">
                        <span className="text-xs text-cyan-400">Envia SMS + WhatsApp com link da Landing Page (configure abaixo na secao Screen Pop)</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Screen Pop Config - Edit Modal */}
          {editarCampanhaData && (
            <div className="border rounded-lg p-4 bg-gradient-to-r from-cyan-950/30 to-purple-950/30 border-cyan-800/30">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-cyan-300">Screen Pop (SMS+WhatsApp com Link)</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editarCampanhaData.screen_pop?.ativo || false}
                    onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), ativo: e.target.checked } }))}
                    className="w-4 h-4 rounded" data-testid="chk-edit-screen-pop-ativo" />
                  <span className="text-xs text-gray-400">Ativar</span>
                </label>
              </div>
              {editarCampanhaData.screen_pop?.ativo && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-400">Titulo da Pagina</label>
                      <Input className="h-8" value={editarCampanhaData.screen_pop?.titulo || ""} placeholder="Ex: Oferta Especial"
                        onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), titulo: e.target.value } }))} />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Nome da Empresa</label>
                      <Input className="h-8" value={editarCampanhaData.screen_pop?.empresa || "StiloCred"}
                        onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), empresa: e.target.value } }))} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Mensagem da Pagina</label>
                    <Input className="h-8" value={editarCampanhaData.screen_pop?.mensagem || ""}
                      onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), mensagem: e.target.value } }))}
                      placeholder="Temos uma oferta especial para voce!" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Texto do SMS (use {"{link}"} para o link)</label>
                    <Input className="h-8" value={editarCampanhaData.screen_pop?.sms_msg || ""}
                      onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), sms_msg: e.target.value } }))} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Texto do WhatsApp (use {"{link}"} para o link)</label>
                    <Input className="h-8" value={editarCampanhaData.screen_pop?.wpp_msg || ""}
                      onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), wpp_msg: e.target.value } }))} />
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={editarCampanhaData.screen_pop?.enviar_sms !== false}
                        onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), enviar_sms: e.target.checked } }))}
                        className="w-3.5 h-3.5 rounded" />
                      <span className="text-xs text-gray-400">Enviar SMS</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={editarCampanhaData.screen_pop?.enviar_wpp !== false}
                        onChange={e => setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), enviar_wpp: e.target.checked } }))}
                        className="w-3.5 h-3.5 rounded" />
                      <span className="text-xs text-gray-400">Enviar WhatsApp</span>
                    </label>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Botoes da Landing Page</label>
                    {(editarCampanhaData.screen_pop?.botoes || []).map((btn, i) => (
                      <div key={i} className="flex gap-2 items-center mb-2">
                        <Input className="flex-1 h-7 text-xs" value={btn.label} placeholder="Texto do botao"
                          onChange={e => {
                            const nb = [...(editarCampanhaData.screen_pop?.botoes || [])];
                            nb[i] = { ...nb[i], label: e.target.value };
                            setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), botoes: nb } }));
                          }} />
                        <select className="h-7 text-xs rounded border bg-gray-900 border-gray-700 px-1"
                          value={btn.estilo || "primary"}
                          onChange={e => {
                            const nb = [...(editarCampanhaData.screen_pop?.botoes || [])];
                            nb[i] = { ...nb[i], estilo: e.target.value };
                            setEditarCampanhaData(p => ({ ...p, screen_pop: { ...(p.screen_pop || {}), botoes: nb } }));
                          }}>
                          <option value="primary">Destaque</option>
                          <option value="secondary">Normal</option>
                        </select>
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" className="text-xs text-cyan-400 h-6 px-2"
                      onClick={() => setEditarCampanhaData(p => ({
                        ...p, screen_pop: { ...(p.screen_pop || {}), botoes: [...(p.screen_pop?.botoes || []), { label: "", acao: "custom", estilo: "secondary" }] }
                      }))}>+ Adicionar Botao</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarCampanha(false)}>Cancelar</Button>
            <Button onClick={salvarEdicaoCampanha} className="bg-purple-600 hover:bg-purple-700">Salvar Alteracoes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
